import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Select,
  Slider,
  Typography,
} from '@mui/material'
import logo from './images/logo.png'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import i18n, { SUPPORTED_LANGUAGES } from '../i18n'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import { setUserWizardSkip } from 'reduxLib/slices/userSlice'
import { setUserWizardDone } from 'reduxLib/slices/userSlice'
import { setSiteUser } from 'reduxLib/slices/userSlice'
import FileDownloadDoneIcon from '@mui/icons-material/FileDownloadDone'
import { wizardAPI } from 'utils/wizardAPI'
import ActivationFlow from './ActivationFlow'
import { makeGetRequest, getUrl } from 'utils/api'
import AccountBalanceIcon from '@mui/icons-material/AccountBalance'
import PeopleIcon from '@mui/icons-material/People'
import HomeIcon from '@mui/icons-material/Home'
import NatureIcon from '@mui/icons-material/Nature'
import AccessTimeIcon from '@mui/icons-material/AccessTime'
import WorkIcon from '@mui/icons-material/Work'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked'
import TextField from '@mui/material/TextField'
import Grid from '@mui/material/Grid'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Divider from '@mui/material/Divider'
import Chip from '@mui/material/Chip'
// const scrollToRef = (ref) => window.scrollTo(0, ref.current.offsetTop)

// LAP-752 — when the backend returns a finance/time profile name in the user's
// language (e.g. 'Cautious' instead of 'Prudent'), the lookup against the
// FR-canonical FINANCE_PROFILE_OUTPUT / TIME_PROFILE_OUTPUT arrays misses and
// the result popup never shows. This helper reverse-maps any localised name
// back to the FR canonical key by walking every language bundle in the
// `wizardMirror.<mappingKey>` block (FR canonical → translation).
//
// Returns the FR canonical name if found, otherwise the original input
// unchanged (so the caller's `.find` keeps its previous behaviour for users
// in FR or for unknown values).
const findCanonicalProfileName = (incoming, mappingKey) => {
  if (!incoming) return incoming
  for (const lang of SUPPORTED_LANGUAGES) {
    let bundle
    try {
      bundle = i18n.getResourceBundle(lang, 'layout')
    } catch (_) {
      bundle = null
    }
    const bag = bundle && bundle.wizardMirror && bundle.wizardMirror[mappingKey]
    if (!bag) continue
    // Direct hit on the FR key (works when backend returned the FR canonical).
    if (bag[incoming]) return incoming
    // Reverse lookup: find the FR key whose translated value equals `incoming`.
    for (const [frKey, translated] of Object.entries(bag)) {
      if (translated === incoming) return frKey
    }
  }
  return incoming
}

// Financial profile result metadata (Phase 1 result popup after /MonPatrimoine flow).
// Populated via localStorage key `w-fprofile-result` which must contain the profile name.
const FINANCE_PROFILE_OUTPUT = [
  {
    name: 'Sans risque',
    text: "Tu privilégies la sécurité à tout prix. Ton capital est précieux, et tu préfères les placements stables, sans surprises. Avec Life, tu vas pouvoir garder le contrôle tout en découvrant des opportunités sûres, parfaitement alignées avec ton besoin de stabilité.",
    logo: '/sans-risque.png',
  },
  {
    name: 'Prudent',
    text: "Tu es mesuré dans tes décisions financières. Tu vises un léger rendement, mais jamais au détriment de la sérénité. Life t'aide à faire les bons choix, au bon moment, en t'accompagnant avec des analyses claires et rassurantes.",
    logo: '/prudent.png',
  },
  {
    name: 'Équilibré',
    text: "Tu cherches le juste milieu entre croissance et protection. Tes choix sont réfléchis, structurés, et alignés avec tes objectifs. Life devient ton allié stratégique pour t'aider à progresser pas à pas, en gardant l'équilibre qui te convient.",
    logo: '/equilibre.png',
  },
  {
    name: 'Dynamique',
    text: "Tu vises la performance. Tu es prêt à accepter des hauts et des bas pour aller chercher du rendement. Avec Life, tu accèdes à des outils puissants pour suivre ton évolution, détecter les opportunités, et ajuster ta stratégie.",
    logo: '/dynamique.png',
  },
  {
    name: 'Offensif',
    text: "Le risque ne te fait pas peur. Tu es ambitieux, curieux, toujours à l'affût d'une nouvelle opportunité. Life t'ouvre le terrain de jeu : alertes, analyses techniques, tendances… pour t'accompagner dans ta quête de performance maximale.",
    logo: '/offensif.png',
  },
]

// Time management profile result metadata (Phase 1 result popup after /syntheseTemps flow).
// Populated via localStorage key `w-tprofile-result` which must contain the profile name.
const TIME_PROFILE_OUTPUT = [
  {
    name: 'Planificateur méthodique',
    text: "Tu es organisé et structuré. Le temps est ta ressource la plus précieuse, et tu sais l'utiliser avec rigueur. Avec Life, tu pourras maintenir ta cadence sans t'épuiser, en équilibrant performance et sérénité.",
    logo: '🧭',
  },
  {
    name: 'Procrastinateur',
    text: "Tu as tendance à attendre la dernière minute, mais tu sais performer sous pression. Life t'aide à anticiper en douceur, sans stress, en rendant la planification naturelle et fluide.",
    logo: '⏳',
  },
  {
    name: 'Multitâche',
    text: "Tu gères beaucoup à la fois, mais parfois au détriment de la concentration. Avec Life, tu vas apprendre à prioriser l'essentiel pour un impact maximal, sans dispersion.",
    logo: '🌀',
  },
  {
    name: 'Spontané',
    text: "Tu vis dans le moment présent, ce qui rend ton quotidien vivant et imprévisible. Life t'offre un cadre léger et flexible pour canaliser ton énergie sans perdre ta spontanéité.",
    logo: '🌊',
  },
  {
    name: 'Perfectionniste',
    text: "Tu recherches l'excellence. Tu aimes que tout soit parfaitement exécuté. Life t'aide à transformer ton exigence en résultats concrets, en évitant la paralysie du détail.",
    logo: '🎯',
  },
  {
    name: 'Productif chronométré',
    text: "Tu es ultra-efficace quand le temps est compté. Les deadlines te boostent. Life synchronise ton rythme naturel avec des objectifs clairs et cadencés.",
    logo: '⏱️',
  },
  {
    name: 'Équilibre travail-vie personnelle',
    text: "Tu as le sens des priorités. Tu veux préserver ton énergie et tes proches. Avec Life, tu structures ton quotidien autour de ce qui compte vraiment pour toi.",
    logo: '⚖️',
  },
  {
    name: 'Amusant',
    text: "Le plaisir est ton moteur. Tu rayonnes, tu inspires, et tu entraînes les autres. Life t'accompagne dans un quotidien joyeux et aligné, où fun et accomplissement cohabitent.",
    logo: '🎉',
  },
  {
    name: 'En difficulté',
    text: "Tu te sens peut-être dépassé, bloqué, ou simplement fatigué. Et c'est ok. Life est là pour toi, sans pression. Pas à pas, on va t'aider à y voir plus clair, t'organiser, et reprendre doucement la main sur ton quotidien. Tu n'as plus à tout porter seul.",
    logo: '🆘',
  },
]

function UserWizardPage({ step }) {
  // GE015 v1.6 §4 — top-level i18n handle, used by the skip / skip-all
  // confirmation dialogs rendered at the end of this component. Sub
  // components (Endwelcome, UserPortrait, WheelOfLife) get their own
  // useTranslation handle so they remain renderable in isolation.
  const { t: tLayout } = useTranslation('layout')
  let wl_done = localStorage.getItem('wl_done')
  let wv_done = localStorage.getItem('wv_done')

  const dispatch = useDispatch()
  const siteUser = useSelector((store) => store.user)

  const user_wizard = siteUser.user?.user_wizard

  const navigate = useNavigate()

  const [wizard, setWizard] = useState({})
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false)
  const [skipAllConfirmOpen, setSkipAllConfirmOpen] = useState(false)
  const [pendingSkipStep, setPendingSkipStep] = useState(null)
  const [loading, setLoading] = useState(false)
  const [contactCount, setContactCount] = useState(0)
  const [accountCount, setAccountCount] = useState(0)
  const [wizardSteps, setWizardSteps] = useState([]) // Store ordered steps array
  const [currentStepIndex, setCurrentStepIndex] = useState(-1) // Current step index
  const [showUserPortrait, setShowUserPortrait] = useState(false)
  // GE015 v1.6 — Wheel of Life is the final wizard screen after the Mirror
  // portrait. A single click on the portrait CTA flips this flag; the next
  // click (on the Wheel of Life CTA) finalises the wizard via handelFinsh.
  const [showWheelOfLife, setShowWheelOfLife] = useState(false)
  // Activation Life (spec Phase 1) — after the Wheel of Life, the user picks
  // priorities, states an intention and confirms their Life Plan. The flow
  // owns the final handelFinsh call (was: the Wheel of Life CTA).
  const [showActivation, setShowActivation] = useState(false)

  useEffect(() => {
    getWizardData()
    // Update wizard steps and current index when user_wizard or step changes
    if (user_wizard && user_wizard.length > 0) {
      setWizardSteps(user_wizard)
      const index = user_wizard.findIndex(w => w.step === step)
      setCurrentStepIndex(index)
      console.log('Updated wizardSteps:', user_wizard.length, 'steps, current index:', index, 'for step:', step)
    }
    // Fetch contact and account counts for status messages
    if (step === 'Social_Contact') {
      fetchContactCount()
    }
    if (step === 'Finance_Account') {
      fetchAccountCount()
    }
    if (wl_done && wv_done) {
      localStorage.removeItem('w-logement')
      localStorage.removeItem('w-vehicule')
      localStorage.removeItem('wl_done')
      localStorage.removeItem('wv_done')
      wizardAPI.updateWizardProgress({ step: step, completed: true, skip: false })
        .then(() => {
          dispatch(setUserWizardDone(step))
          navigateAfterStepCompletion()
        })
        .catch((error) => {
          console.error('Error updating wizard progress:', error)
        })
    }
    // Perso_Target (définition des 25 objectifs) removed from the onboarding:
    // the wdo_done/wsr_done completion handshake is gone with it. The
    // /definir-objective page remains available outside the wizard (quick add).
  }, [step])

  const fetchContactCount = async () => {
    try {
      const { data } = await makeGetRequest('/api/Social/get-all-contacts/', {}, true)
      setContactCount(data?.length || 0)
    } catch (error) {
      console.error('Error fetching contact count:', error)
    }
  }

  const fetchAccountCount = async () => {
    try {
      const response = await makeGetRequest('/api/Finance/Accounts/', {}, true)
      const data = response?.status === 200 ? response?.data : null
      setAccountCount(Array.isArray(data) ? data.length : 0)
    } catch (error) {
      console.error('Error fetching account count:', error)
    }
  }

  const getWizardData = async () => {
    setLoading(true)
    try {
      const data = await wizardAPI.getWizardStep(step)
      console.log('Wizard data received:', data) // Debug log
      if (data && Object.keys(data).length > 0) {
      setWizard(data)
      } else {
        console.warn('Empty wizard data received for step:', step)
        alert(tLayout('wizardErrors.noData'))
      }
    } catch (error) {
      if (error.message === 'SUBSCRIPTION_REQUIRED') {
        alert(tLayout('wizardErrors.subscriptionRequired'))
        // Navigate to next step or home
        navigate('/')
      } else if (error.message === 'STEP_NOT_FOUND') {
        console.error('Step not found:', step)
        // Navigate to home if step doesn't exist
        navigate('/')
      } else {
        console.error('Error loading wizard step:', error)
        alert(tLayout('wizardErrors.loadStep'))
      }
    } finally {
      setLoading(false)
    }
  }

  // Helper function to find next step using index-based navigation
  const findNextStepByIndex = (stepsArray, currentStepOrIndex) => {
    if (!stepsArray || stepsArray.length === 0) {
      console.log('findNextStepByIndex: stepsArray is empty or null')
      return null
    }
    
    // Accept either step name (string) or index (number)
    let currentIndex = -1
    if (typeof currentStepOrIndex === 'number') {
      currentIndex = currentStepOrIndex
    } else {
      // Find current step index by name
      currentIndex = stepsArray.findIndex(w => w.step === currentStepOrIndex)
    }
    
    if (currentIndex === -1 || currentIndex < 0) {
      console.log('findNextStepByIndex: Current step/index not found in array')
      return null
    }
    
    console.log(`findNextStepByIndex: Starting from index ${currentIndex} of ${stepsArray.length}`)
    console.log(`findNextStepByIndex: Current step: ${stepsArray[currentIndex]?.step}, completed: ${stepsArray[currentIndex]?.completed}, skip: ${stepsArray[currentIndex]?.skip}`)
    
    // Find next incomplete step after current index
    console.log(`findNextStepByIndex: Searching from index ${currentIndex + 1} to ${stepsArray.length - 1}`)
    for (let i = currentIndex + 1; i < stepsArray.length; i++) {
      const wizardStep = stepsArray[i]
      if (!wizardStep) {
        console.log(`findNextStepByIndex: Warning - no step at index ${i}`)
        continue
      }
      console.log(`findNextStepByIndex: Checking index ${i}: ${wizardStep.step} - completed: ${wizardStep.completed}, skip: ${wizardStep.skip}`)
      if (!wizardStep.completed && !wizardStep.skip) {
        console.log(`findNextStepByIndex: ✓ Found next step at index ${i}:`, wizardStep.step)
        return wizardStep.step
      } else {
        console.log(`findNextStepByIndex: ✗ Skipping index ${i} (completed: ${wizardStep.completed}, skip: ${wizardStep.skip})`)
      }
    }
    
    // If no next incomplete step found, wizard is complete
    console.log('findNextStepByIndex: No next incomplete step found - wizard complete')
    return null
  }
  
  // Calculate progress percentage based on current step index
  const calculateProgress = (stepsArray, currentStep) => {
    if (!stepsArray || stepsArray.length === 0) return 0
    
    const currentIndex = stepsArray.findIndex(w => w.step === currentStep)
    if (currentIndex === -1) return 0
    
    // Progress = (currentIndex + 1) / totalSteps * 100
    const progress = Math.round(((currentIndex + 1) / stepsArray.length) * 100)
    return Math.min(progress, 100) // Cap at 100%
  }

  // Helper function to handle navigation after step completion
  const navigateAfterStepCompletion = async () => {
    setLoading(true) // Show loading spinner during navigation
    try {
      // Refresh user data from backend to get updated wizard progress FIRST
      const userResponse = await makeGetRequest('/api/user/user/', {}, true)
      if (userResponse?.data) {
        // Ensure first_login_completed is not set to true if there are still incomplete steps
        const updatedUser = { ...userResponse.data }
        const user_wizard = updatedUser.user_wizard || []
        const hasIncompleteSteps = user_wizard.some(step => !step.completed && !step.skip)
        
        // If there are incomplete steps, ensure first_login_completed is false
        if (hasIncompleteSteps && updatedUser.first_login_completed === true) {
          updatedUser.first_login_completed = false
        }
        
        // CRITICAL: Refresh wizard data to get updated categories list
        // This ensures the categories list shows the correct completion status immediately
        try {
          const wizardResponse = await wizardAPI.getWizardStep(step)
          if (wizardResponse?.categories) {
            // Update wizard state with fresh categories data
            setWizard(prevWizard => ({
              ...prevWizard,
              categories: wizardResponse.categories,
              categories_completed: wizardResponse.categories_completed || 0,
              curent_prog: wizardResponse.curent_prog || 0
            }))
            console.log('✅ Refreshed categories after step completion:', wizardResponse.categories)
          }
        } catch (error) {
          console.error('Could not refresh wizard data for categories:', error)
        }
        
        // CRITICAL: Check if backend data is corrupted (all steps marked as skipped)
        const skippedCount = user_wizard.filter(w => w.skip).length
        const totalCount = user_wizard.length
        if (skippedCount === totalCount && totalCount > 1) {
          console.error('⚠️ BACKEND DATA CORRUPTION DETECTED: All steps are marked as skipped!')
          console.error('This should not happen - only the skipped step should have skip=true')
          console.error('Skipped steps:', user_wizard.filter(w => w.skip).map(w => w.step))
          // Don't use corrupted data - try to get fresh data from wizard API instead
          console.warn('Attempting to use wizard API data instead...')
        }
        
        // CRITICAL: If backend data is corrupted (all steps skipped), don't update Redux with bad data
        // Instead, try to get correct data from the wizard API for the current step
        if (skippedCount === totalCount && totalCount > 1) {
          console.error('⚠️ BACKEND DATA CORRUPTION: All steps marked as skipped')
          console.error('Attempting to get correct data from wizard API...')
          
          try {
            // Get wizard data for current step - this should have correct user_wizards array
            const wizardResponse = await wizardAPI.getWizardStep(step)
            if (wizardResponse?.user_wizards && wizardResponse.user_wizards.length > 0) {
              // Use the user_wizards from wizard API instead
              const correctUserWizard = wizardResponse.user_wizards
              const correctSkippedCount = correctUserWizard.filter(w => w.skip).length
              console.log('Wizard API data: skipped count =', correctSkippedCount, 'of', correctUserWizard.length)
              
              if (correctSkippedCount < totalCount) {
                // Wizard API has correct data - use it
                updatedUser.user_wizard = correctUserWizard
                user_wizard = correctUserWizard
                console.log('✅ Using corrected user_wizard from wizard API')
              }
            }
          } catch (error) {
            console.error('Could not get corrected data from wizard API:', error)
          }
        }
        
        dispatch(setSiteUser(updatedUser))
        
        // Find current step's index in the FRESH user_wizard array (not the stale stored index)
        // This ensures we're using the most up-to-date data
        const currentIndexInFreshArray = user_wizard.findIndex(w => w.step === step)
        console.log('Current step index in fresh array:', currentIndexInFreshArray, 'for step:', step)
        console.log('Fresh user_wizard array (first 5):', user_wizard.slice(0, 5).map((w, i) => ({ index: i, step: w.step, completed: w.completed, skip: w.skip })))
        console.log('Skipped count:', user_wizard.filter(w => w.skip).length, 'of', user_wizard.length)
        
        // Use the fresh array index to find next step
        // If all steps are skipped (data corruption), we'll still try to find the next one
        const nextStep = findNextStepByIndex(user_wizard, currentIndexInFreshArray >= 0 ? currentIndexInFreshArray : step)
        
        console.log('Using next step (index-based):', nextStep, 'for current step:', step)
        console.log('Progress:', calculateProgress(user_wizard, step), '%')
        
        if (nextStep) {
          // Small delay to ensure state is updated before App.js re-evaluates
          setTimeout(() => {
            // Navigate to home - App.js will show the next step based on updated user_wizard state
            navigate('/')
            setLoading(false) // Hide loading after navigation
          }, 100)
          return
        }
      }
    } catch (error) {
      console.error('Error refreshing user data:', error)
      setLoading(false) // Hide loading on error
    }
    
    // If no next step found, navigate to home (wizard complete or error)
    setLoading(false) // Hide loading
    navigate('/')
  }

  const handelWelcome = async () => {
    try {
      await wizardAPI.updateWizardProgress({ step: step, completed: true, skip: false })
      await dispatch(setUserWizardDone(step))
      await navigateAfterStepCompletion()
    } catch (error) {
      console.error('Error updating wizard progress:', error)
      alert(tLayout('wizardErrors.save'))
    }
  }

  const handelFinsh = async () => {
    // LAP-736: navigate immediately to SmartFeed (rendered at "/" via HomePage)
    // and run wizard-finalisation calls in the background. The previous
    // implementation awaited two sequential API calls before navigating, which
    // caused a multi-second delay at a critical emotional moment.
    try {
      dispatch(setUserWizardDone(step))
      navigate('/')
      // Fire-and-forget background cleanup. Errors are logged, not surfaced to
      // the user — the navigation is already done and a retry isn't required:
      // skipAllWizardSteps is idempotent and the user reload will re-fetch
      // /api/user/user/ on next request.
      Promise.allSettled([
        wizardAPI.skipAllWizardSteps(),
        makeGetRequest('/api/user/user/', {}, true).then((userResponse) => {
          if (userResponse?.data) {
            dispatch(setSiteUser(userResponse.data))
          }
        }),
      ]).then((results) => {
        const failures = results.filter((r) => r.status === 'rejected')
        if (failures.length) {
          console.error('Wizard finalisation background errors:', failures.map((f) => f.reason))
        }
      })
    } catch (error) {
      console.error('Error finishing wizard:', error)
    }
  }

  /** End screen: finish wizard and go to home with flag to show user portrait (4.13.1.2) */
  const handleDiscoverPortrait = async () => {
    try {
      await wizardAPI.skipAllWizardSteps()
      await dispatch(setUserWizardDone(step))
      const userResponse = await makeGetRequest('/api/user/user/', {}, true)
      if (userResponse?.data) {
        dispatch(setSiteUser(userResponse.data))
      }
      navigate('/', { state: { openUserPortrait: true } })
    } catch (error) {
      console.error('Error finishing wizard:', error)
      alert(tLayout('wizardErrors.finalize'))
    }
  }

  const handelSkip = () => {
    // Show confirmation dialog (spec 4.11.2)
    setPendingSkipStep(step)
    setSkipConfirmOpen(true)
  }

  // Validate current step and go to next (no auto-next; user clicks "Valider")
  const handleValidateStep = async () => {
    try {
      await wizardAPI.updateWizardProgress({ step, completed: true, skip: false })
      await dispatch(setUserWizardDone(step))
      await navigateAfterStepCompletion()
    } catch (error) {
      console.error('Error validating step:', error)
      alert(tLayout('wizardErrors.validate'))
    }
  }

  const confirmSkip = async () => {
    if (pendingSkipStep) {
      try {
        // Only skip the specific step, not all steps
        await wizardAPI.updateWizardProgress({ step: pendingSkipStep, completed: false, skip: true })
        await dispatch(setUserWizardSkip(pendingSkipStep))
        
        // Refresh user data from backend to get updated wizard progress
        try {
          const userResponse = await makeGetRequest('/api/user/user/', {}, true)
          if (userResponse?.data) {
            // Ensure first_login_completed is not set to true if there are still incomplete steps
            const updatedUser = { ...userResponse.data }
            const user_wizard = updatedUser.user_wizard || []
            const hasIncompleteSteps = user_wizard.some(step => !step.completed && !step.skip)
            
            // If there are incomplete steps, ensure first_login_completed is false
            if (hasIncompleteSteps && updatedUser.first_login_completed === true) {
              updatedUser.first_login_completed = false
            }
            
            dispatch(setSiteUser(updatedUser))
          }
        } catch (error) {
          console.error('Error refreshing user data:', error)
        }
        
        setSkipConfirmOpen(false)
        setPendingSkipStep(null)
        // Navigate to next step or home
        await navigateAfterStepCompletion()
      } catch (error) {
        console.error('Error skipping wizard step:', error)
        alert(tLayout('wizardErrors.skipStep'))
      }
    }
  }

  const cancelSkip = () => {
    setSkipConfirmOpen(false)
    setPendingSkipStep(null)
  }

  const handleSkipAll = () => {
    // Show confirmation dialog instead of window.confirm (spec 4.11.1, 4.11.2)
    setSkipAllConfirmOpen(true)
  }

  const confirmSkipAll = async () => {
    try {
      await wizardAPI.skipAllWizardSteps()
      setSkipAllConfirmOpen(false)
      // Refetch user so first_login_completed is in Redux before navigate; App.js then shows HomePage
      const userResponse = await makeGetRequest('/api/user/user/', {}, true)
      if (userResponse?.data) {
        dispatch(setSiteUser(userResponse.data))
      }
      navigate('/')
    } catch (error) {
      console.error('Error skipping all wizard steps:', error)
      alert(tLayout('wizardErrors.skip'))
    }
  }

  const cancelSkipAll = () => {
    setSkipAllConfirmOpen(false)
  }

  // Safe display value: never show NaN, null, or undefined (used in layout and evaluation steps)
  const safeDisplay = (v) => {
    if (v == null || v === '') return ''
    if (typeof v === 'number' && Number.isNaN(v)) return ''
    const s = String(v).trim()
    if (s.toLowerCase() === 'nan' || s === 'undefined' || s.toLowerCase() === 'null') return ''
    return s
  }

  // Standard Step Layout Component (70/30 split) - Enhanced per GE015 spec
  const StandardStepLayout = ({ data, children, buttons, statusMessage }) => {
    const titleContent = data ? safeDisplay(data.title_content) : ''
    const subtitleContent = data ? safeDisplay(data.subtitle) : ''
    return (
      <Box 
        color={'white'} 
        display={'flex'} 
        flexDirection={{ xs: 'column', lg: 'row' }}
        gap={{ xs: 3, lg: 5 }}
        mt={0}
        pt={0}
        minHeight={'85vh'}
        pb={{ xs: 5, md: 7 }}
        px={{ xs: 2, md: 4 }}
        sx={{
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Box 
          width={{ xs: '100%', lg: '70%' }}
          p={{ xs: 2, md: 3 }}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
          }}
        >
          {/* Header Section */}
          <Box>
            <Box 
              display={'flex'} 
              alignItems={'center'} 
              mb={4}
              sx={{
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 2, sm: 2.5 }
              }}
            >
              {data?.logo && (
                <Box 
                  sx={{ 
                    img: { 
                      height: { xs: '60px', sm: '80px', md: '100px' }, 
                      width: 'auto', 
                      objectFit: 'contain',
                      filter: 'drop-shadow(0 2px 8px rgba(0, 137, 255, 0.3))'
                    } 
                  }}
                >
                  <img 
                    src={data.logo} 
                    alt="Life" 
                    onError={(e) => { e.target.onerror = null; e.target.src = logo; e.target.alt = 'Life' }}
                  />
        </Box>
              )}
        <Box>
                {titleContent && (
                  <Box 
                    fontSize={{ xs: 22, sm: 26, md: 28 }} 
                    fontWeight={700} 
                    lineHeight={1.2}
                    mb={0.5}
                    sx={{
                      background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.9) 100%)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    {titleContent}
                  </Box>
                )}
                {subtitleContent && (
                  <Box 
                    color={'rgba(255, 255, 255, 0.75)'} 
                    fontSize={{ xs: 14, sm: 16, md: 18 }} 
                    fontWeight={500}
                    sx={{
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {subtitleContent}
                  </Box>
                )}
              </Box>
            </Box>

            {/* Content Section */}
            {safeDisplay(data?.text_content) && (
              <Box 
                mt={4} 
                fontSize={{ xs: 16, sm: 18, md: 20 }} 
                lineHeight={1.7}
                sx={{ maxWidth: '900px' }}
                color={'rgba(255, 255, 255, 0.95)'}
              >
                {safeDisplay(data.text_content)}
              </Box>
            )}
            
            {/* Children content */}
            {children}
            
            {/* Show message if no content and no children */}
            {!data?.text_content && !children && (
              <Box 
                mt={4} 
                fontSize={{ xs: 16, sm: 18 }} 
                color={'rgba(255, 255, 255, 0.7)'}
                textAlign="center"
                py={4}
              >
                Cette étape ne nécessite pas de contenu supplémentaire.
              </Box>
            )}
            
            {/* Status message - Enhanced styling */}
            {statusMessage && (
              <Box
                mt={4}
                p={2}
                sx={{
                  border: '2px solid rgba(0, 137, 255, 0.4)',
                  borderRadius: 2,
                  backgroundColor: 'rgba(0, 137, 255, 0.1)',
                  backdropFilter: 'blur(10px)',
                  boxShadow: '0 4px 12px rgba(0, 137, 255, 0.2)',
                }}
              >
                <Typography 
                  fontSize={{ xs: 15, md: 17 }} 
                  color={'white'}
                  fontWeight={500}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <CheckCircleIcon sx={{ color: '#2ca948', fontSize: 20 }} />
                  {statusMessage}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Action Buttons Section - Enhanced */}
          <Box 
            mt={{ xs: 5, md: 6 }} 
                display={'flex'}
            gap={2} 
            flexWrap={'wrap'}
            sx={{
              '& > button': {
                minWidth: { xs: '100%', sm: '180px' },
                py: 1.5,
                px: 3,
                fontSize: { xs: 16, md: 18 },
                fontWeight: 600,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(0, 0, 0, 0.25)',
                },
              },
            }}
          >
            {buttons}
          </Box>
        </Box>
        
        {/* Progress Configuration Sidebar */}
        <ProgConfig data={wizard} />
      </Box>
    )
  }

  // Helper function for consistent button styling
  const getButtonStyles = (variant = 'primary') => {
    const baseStyles = {
      fontWeight: 600,
      borderRadius: 2,
      textTransform: 'none',
      transition: 'all 0.3s ease',
    }
    
    if (variant === 'primary') {
      return {
        ...baseStyles,
                  backgroundColor: '#0089ff',
        background: 'linear-gradient(135deg, #0089ff 0%, #00a8ff 100%)',
        color: 'white',
        boxShadow: '0 4px 12px rgba(0, 137, 255, 0.3)',
        '&:hover': {
          backgroundColor: '#0070cc',
          background: 'linear-gradient(135deg, #0070cc 0%, #0089ff 100%)',
          transform: 'translateY(-2px)',
          boxShadow: '0 6px 16px rgba(0, 137, 255, 0.4)',
        },
      }
    }
    
    if (variant === 'outlined') {
      return {
        ...baseStyles,
        color: 'white',
        borderColor: 'rgba(255, 255, 255, 0.4)',
        borderWidth: 2,
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        backdropFilter: 'blur(10px)',
        '&:hover': {
          borderColor: 'rgba(255, 255, 255, 0.8)',
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          transform: 'translateY(-2px)',
          boxShadow: '0 4px 12px rgba(255, 255, 255, 0.2)',
        },
      }
    }
    
    return baseStyles
  }

  // Icon mapping for categories
  const getCategoryIcon = (categoryName) => {
    // LAP-752: matchers cover FR + EN + DE + IT + ES so the right icon is
    // selected after the backend translates category labels.
    const name = (categoryName || '').toLowerCase()
    if (name.includes('introduction') || name.includes('start') || name.includes('einführ') || name.includes('inicio')) {
      return <EmojiEventsIcon sx={{ fontSize: 20 }} />
    }
    if (name.includes('social') || name.includes('connexion') || name.includes('connection') || name.includes('beziehung') || name.includes('relación')) {
      return <PeopleIcon sx={{ fontSize: 20 }} />
    }
    if (name.includes('financi') || name.includes('finance') || name.includes('finanz') || name.includes('finanze')) {
      return <AccountBalanceIcon sx={{ fontSize: 20 }} />
    }
    if (name.includes('foyer') || name.includes('home') || name.includes('harmonie') || name.includes('harmony') || name.includes('zuhause') || name.includes('hogar') || name.includes('casa')) {
      return <HomeIcon sx={{ fontSize: 20 }} />
    }
    if (name.includes('eco') || name.includes('impact') || name.includes('environ') || name.includes('öko') || name.includes('umwelt') || name.includes('huella')) {
      return <NatureIcon sx={{ fontSize: 20 }} />
    }
    if (name.includes('temps') || name.includes('time') || name.includes('gestion') || name.includes('zeit') || name.includes('tiempo') || name.includes('tempo')) {
      return <AccessTimeIcon sx={{ fontSize: 20 }} />
    }
    if (name.includes('profession') || name.includes('work') || name.includes('ascension') || name.includes('carrière') || name.includes('career') || name.includes('beruf') || name.includes('lavoro') || name.includes('trabajo')) {
      return <WorkIcon sx={{ fontSize: 20 }} />
    }
    if (name.includes('personnel') || name.includes('personal') || name.includes('epanouissement') || name.includes('épanouissement') || name.includes('fulfil') || name.includes('persönlich') || name.includes('personale') || name.includes('crecimiento') || name.includes('objectif') || name.includes('end')) {
      return <EmojiEventsIcon sx={{ fontSize: 20 }} />
    }
    // Default icon
    return <CheckCircleIcon sx={{ fontSize: 20 }} />
  }

  const ProgConfig = ({ data }) => {
    // Calculate progress based on current step index in wizard steps array
    let progressPercentage = 0
    if (wizardSteps.length > 0 && currentStepIndex >= 0) {
      // Progress = (currentIndex + 1) / totalSteps * 100
      progressPercentage = Math.round(((currentStepIndex + 1) / wizardSteps.length) * 100)
      progressPercentage = Math.min(progressPercentage, 100) // Cap at 100%
    } else {
      // Fallback to category-based progress from backend
      progressPercentage = wizard.curent_prog || wizard.progress_percentage || 0
    }
    
    return (
      <Box 
        width={{ xs: '100%', lg: '30%' }}
        sx={{
          position: { lg: 'sticky' },
          top: { lg: 20 },
          alignSelf: { lg: 'flex-start' },
          maxHeight: { lg: '90vh' },
          overflowY: { lg: 'auto' },
          px: { xs: 2, md: 0 },
        }}
      >
        {/* Header - Enhanced */}
        <Box 
          textAlign={'center'} 
          fontSize={{ xs: 20, md: 24 }} 
          fontWeight={700} 
          mb={3}
          mt={2}
          sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, rgba(255, 255, 255, 0.8) 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '0.5px',
          }}
        >
          {tLayout('wizardProgress.title')}
              </Box>
        
        {/* Enhanced Progress Bar */}
        <Box mb={2}>
          <Box
            sx={{
              width: '100%',
              height: '12px',
              backgroundColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: '12px',
              overflow: 'hidden',
              position: 'relative',
              boxShadow: 'inset 0 2px 4px rgba(0, 0, 0, 0.2)',
            }}
          >
            <Box
              sx={{
                width: `${progressPercentage}%`,
                height: '100%',
                background: 'linear-gradient(90deg, #0089ff 0%, #00a8ff 100%)',
                borderRadius: '12px',
                transition: 'width 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'flex-end',
                paddingRight: '6px',
                boxShadow: '0 2px 8px rgba(0, 137, 255, 0.4)',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.3) 100%)',
                  borderRadius: '12px',
                },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: 'white',
                  fontSize: '11px',
                  fontWeight: 700,
                  lineHeight: 1,
                  textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
                  zIndex: 1,
                }}
              >
                {progressPercentage}%
              </Typography>
            </Box>
          </Box>
          {/* Progress Text */}
          <Box 
            mt={1.5} 
            textAlign={'center'}
            fontSize={{ xs: 13, md: 14 }}
            color={'rgba(255, 255, 255, 0.7)'}
            fontWeight={500}
          >
            {tLayout('wizardProgress.categoriesCompleted', {
              completed: wizard.categories_completed || 0,
              total: wizard.categories_total || 0,
            })}
        </Box>
        </Box>

        {/* Categories List - Enhanced Modern Design */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1, // Reduced from 1.5 to 1 for better spacing
            maxHeight: { xs: '300px', md: '400px' }, // Limit height to prevent overflow
            overflowY: 'auto', // Allow scrolling if needed
            pr: 1, // Padding for scrollbar
            '&::-webkit-scrollbar': {
              width: '6px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '3px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(255, 255, 255, 0.2)',
              borderRadius: '3px',
              '&:hover': {
                background: 'rgba(255, 255, 255, 0.3)',
              },
            },
          }}
        >
          {/* Display categories dynamically from backend (v1.1); exclude START and END from list */}
          {wizard?.categories
            ?.filter((c) => {
              const name = (c?.name || '').toUpperCase()
              return name !== 'START' && name !== 'END'
            })
            ?.map((category, index) => {
            const isCompleted = category.completed
            const Icon = getCategoryIcon(category.name)
            
            return (
              <Box
                key={category.name || index}
              sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  padding: '12px 16px',
                  borderRadius: '12px',
                  backgroundColor: isCompleted 
                    ? 'rgba(44, 169, 72, 0.15)' 
                    : 'rgba(255, 255, 255, 0.05)',
                  border: isCompleted 
                    ? '2px solid rgba(44, 169, 72, 0.4)' 
                    : '1px solid rgba(255, 255, 255, 0.15)',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  cursor: 'pointer',
                  backdropFilter: 'blur(10px)',
                  boxShadow: isCompleted 
                    ? '0 4px 12px rgba(44, 169, 72, 0.2)' 
                    : '0 2px 8px rgba(0, 0, 0, 0.1)',
                  '&:hover': {
                    backgroundColor: isCompleted 
                      ? 'rgba(44, 169, 72, 0.2)' 
                      : 'rgba(255, 255, 255, 0.08)',
                    transform: 'translateX(4px)',
                    borderColor: isCompleted 
                      ? 'rgba(44, 169, 72, 0.6)' 
                      : 'rgba(255, 255, 255, 0.25)',
                    boxShadow: isCompleted 
                      ? '0 6px 16px rgba(44, 169, 72, 0.3)' 
                      : '0 4px 12px rgba(0, 0, 0, 0.15)',
                  },
                }}
              >
                {/* Icon - Enhanced */}
                <Box
                  sx={{
                    color: isCompleted ? '#2ca948' : 'rgba(255, 255, 255, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: '28px',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {Icon}
            </Box>
                
                {/* Category Name - Enhanced */}
                <Box
                  sx={{
                    flex: 1,
                    fontSize: { xs: 14, md: 16 },
                    fontWeight: isCompleted ? 600 : 500,
                    color: isCompleted ? 'white' : 'rgba(255, 255, 255, 0.75)',
                    lineHeight: 1.4,
                    letterSpacing: '0.2px',
                  }}
                >
                  {category.name}
          </Box>
                
                {/* Check Icon - Enhanced */}
                <Box
                  sx={{
                    color: isCompleted ? '#2ca948' : 'rgba(255, 255, 255, 0.25)',
                    display: 'flex',
                    alignItems: 'center',
                    transition: 'all 0.3s ease',
                  }}
                >
                  {isCompleted ? (
                    <CheckCircleIcon 
                      sx={{ 
                        fontSize: 24,
                        filter: 'drop-shadow(0 2px 4px rgba(44, 169, 72, 0.4))',
                      }} 
                    />
                  ) : (
                    <RadioButtonUncheckedIcon 
                      sx={{ 
                        fontSize: 24,
                        opacity: 0.5,
                      }} 
                    />
                  )}
                </Box>
              </Box>
            )
          })}
          
          {/* Fallback to user_wizards if categories not available; exclude START and END */}
          {(!wizard?.categories || wizard.categories.length === 0) && wizard?.user_wizards
            ?.filter((w) => {
              const t = (w?.title || '').toUpperCase()
              return t !== 'START' && t !== 'END'
            })
            ?.map((wizardItem, index) => {
            const isCompleted = wizardItem.completed
            const Icon = getCategoryIcon(wizardItem.title)
            
            return (
              <Box
                key={wizardItem.title || index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  padding: '8px 12px',
                  borderRadius: '8px',
                  backgroundColor: isCompleted 
                    ? 'rgba(44, 169, 72, 0.1)' 
                    : 'rgba(255, 255, 255, 0.03)',
                  border: isCompleted 
                    ? '1px solid rgba(44, 169, 72, 0.3)' 
                    : '1px solid rgba(255, 255, 255, 0.1)',
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    backgroundColor: isCompleted 
                      ? 'rgba(44, 169, 72, 0.15)' 
                      : 'rgba(255, 255, 255, 0.05)',
                  },
                }}
              >
                {/* Icon */}
                <Box
                  sx={{
                    color: isCompleted ? '#2ca948' : 'rgba(255, 255, 255, 0.4)',
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: '24px',
                  }}
                >
                  {Icon}
                </Box>
                
                {/* Category Name */}
                <Box
                  sx={{
                    flex: 1,
                    fontSize: { xs: 14, md: 15 },
                    fontWeight: isCompleted ? 600 : 500,
                    color: isCompleted ? 'white' : 'rgba(255, 255, 255, 0.6)',
                    lineHeight: 1.3,
                  }}
                >
                  {wizardItem.title}
                </Box>
                
                {/* Check Icon */}
                <Box
                  sx={{
                    color: isCompleted ? '#2ca948' : 'rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {isCompleted ? (
                    <CheckCircleIcon sx={{ fontSize: 20 }} />
                  ) : (
                    <RadioButtonUncheckedIcon sx={{ fontSize: 20 }} />
                  )}
                </Box>
              </Box>
            )
          })}
        </Box>
      </Box>
    )
  }
  // Start screen image: prefer API logo (S3 URL in staging) to avoid 404 from backend /media/
  const mediaGuide = getUrl() + '/media/Guide/'
  const START_SCREEN_IMAGE_FALLBACK = mediaGuide + 'Life.png'

  const Welcome = ({ data }) => {
    if (!data) {
      return (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="85vh" color="white">
          <Typography>Chargement...</Typography>
        </Box>
      )
    }

    // API returns full S3 URL in staging; backend /media/ returns 404 when media is on S3
    const logoSrc = (data.logo && (data.logo.startsWith('http') || data.logo.startsWith('/')))
      ? (data.logo.startsWith('http') ? data.logo : getUrl() + data.logo)
      : START_SCREEN_IMAGE_FALLBACK

    return (
      <Box
        display="flex"
        justifyContent="center"
        flexDirection="column"
        alignItems="center"
        color="white"
        minHeight="85vh"
        px={{ xs: 3, md: 4 }}
        pt={0}
        pb={{ xs: 4, md: 6 }}
        sx={{ position: 'relative', zIndex: 1 }}
      >
        {/* 1. Title: "Bienvenue dans" (above the LIFE logo) */}
        {/* <Box
          component="span"
          sx={{
            fontSize: { xs: 26, sm: 32, md: 38 },
            fontWeight: 600,
            color: 'white',
            textAlign: 'center',
            display: 'block',
            letterSpacing: '-0.02em',
          }}
        >
          {tLayout('wizardWelcome.welcomeTo')}
        </Box> */}

        {/* 2. LIFE logo image (Life.png from media/Guide or API S3 URL) */}
        <Box
          sx={{
            '& img': {
              height: { xs: '120px', sm: '150px', md: '180px' },
              width: 'auto',
              maxWidth: '100%',
              display: 'block',
              objectFit: 'contain',
            },
            mt: 1,
          }}
        >
          <img
            src={logoSrc}
            alt="LIFE"
            onError={(e) => {
              e.target.onerror = null
              e.target.src = logoSrc === START_SCREEN_IMAGE_FALLBACK ? mediaGuide + 'life.png' : START_SCREEN_IMAGE_FALLBACK
              if (e.target.src === mediaGuide + 'life.png') {
                e.target.onerror = () => { e.target.onerror = null; e.target.src = logo }
              }
              e.target.alt = 'LIFE'
            }}
          />
        </Box>

        {/* 3. Subtitle: "Un nouveau chapitre commence !" */}
        <Box
          mt={2}
          sx={{
            fontSize: { xs: 18, sm: 20, md: 22 },
            fontWeight: 500,
            color: 'rgba(255, 255, 255, 0.95)',
            textAlign: 'center',
          }}
        >
          {tLayout('wizardWelcome.newChapter')}
        </Box>

        <Box
          mt={3}
          sx={{
            fontSize: { xs: 16, sm: 17, md: 18 },
            lineHeight: 1.7,
            textAlign: 'center',
            maxWidth: '800px',
            color: 'rgba(255, 255, 255, 0.9)',
            px: { xs: 2, md: 0 },
          }}
        >
          {safeDisplay(data?.text_content) || tLayout('wizardWelcome.introText')}
        </Box>

        {/* 5. Single CTA: "Entrez dans life" */}
        <Box mt={6} display="flex" flexDirection="column" alignItems="center" width="100%" maxWidth="400px">
            <Button
                variant="contained"
                onClick={handelWelcome}
                sx={{
                  width: '100%',
                  maxWidth: 320,
                  py: 1,
                  px: 4,
                  fontSize: 18,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  backgroundColor: '#005ecb', 
                  color: 'white',
                  boxShadow: 'none', 
                  '&:hover': {
                    backgroundColor: '#004aa3', 
                    boxShadow: 'none',
                  },
                }}
              >

            {data.text_button_1 || tLayout('wizardWelcome.enterButton')}
          </Button>

          <Button
            variant="text"
            onClick={() => handleSkipAll()}
            sx={{
              mt: 2,
              color: 'rgba(255, 255, 255, 0.7)',
              textTransform: 'none',
              fontSize: 14,
              '&:hover': {
                color: 'rgba(255, 255, 255, 0.9)',
                backgroundColor: 'transparent',
              },
            }}
          >
            {tLayout('wizardWelcome.skipOnboarding')}
          </Button>
        </Box>
      </Box>
    )
  }
  // Summary Screen Components (Specs 4.4, 4.6, 4.7, 4.8)
  
  // Financial Profile Summary (Spec 4.4)
  const FinancialProfileSummary = ({ data }) => {
    const [summary, setSummary] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
      loadSummary()
    }, [])

    const loadSummary = async () => {
      try {
        const data = await wizardAPI.getFinancialProfileSummary()
        setSummary(data)
      } catch (error) {
        console.error('Error loading financial profile summary:', error)
      } finally {
        setLoading(false)
      }
    }

    const handleContinue = async () => {
      await wizardAPI.updateWizardProgress({ step: step, completed: true, skip: false })
      await dispatch(setUserWizardDone(step))
      await navigateAfterStepCompletion()
    }

    if (loading) {
      return (
        <StandardStepLayout data={data} buttons={[]}>
          <Box textAlign="center" py={4}>
            <Typography color="white">Chargement...</Typography>
          </Box>
        </StandardStepLayout>
      )
    }

    return (
      <StandardStepLayout
        data={data}
        buttons={[
          <Button key="continue" variant="contained" sx={getButtonStyles('primary')} onClick={handleContinue}>
            {data.text_button_1 || tLayout('wizardButtons.continue')}
          </Button>
        ]}
      >
        {summary && (
        <Box mt={4}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" color="white" mb={2} fontWeight={600}>
                      Profil Financier
                    </Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" mb={1}>
                      Profil: <strong style={{ color: 'white' }}>{summary.profile}</strong>
                    </Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" mb={1}>
                      Connaissances: <strong style={{ color: 'white' }}>{summary.financial_knowledge}</strong>
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" color="white" mb={2} fontWeight={600}>
                      Revenus & Dépenses
                    </Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" mb={1}>
                      Revenus actuels: <strong style={{ color: '#2ca948' }}>{summary.current_income?.toLocaleString()} €</strong>
                    </Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" mb={1}>
                      Revenus cibles: <strong style={{ color: '#0089ff' }}>{summary.target_income?.toLocaleString()} €</strong>
                    </Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" mb={1}>
                      Dépenses actuelles: <strong style={{ color: '#e91e63' }}>{summary.current_expense?.toLocaleString()} €</strong>
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" color="white" mb={2} fontWeight={600}>
                      Patrimoine Net
                    </Typography>
                    <Typography variant="h5" color="#2ca948" mb={1}>
                      {summary.current_net_worth?.toLocaleString()} €
                    </Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" mb={2}>
                      Objectif: {summary.target_net_worth?.toLocaleString()} €
                    </Typography>
                    {summary.recommendation_text && (
                      <Box mt={2} p={2} sx={{ backgroundColor: 'rgba(0, 137, 255, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" color="white">
                          {summary.recommendation_text}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </StandardStepLayout>
    )
  }

  // Eco-Footprint Summary (Spec 4.6)
  const EcoFootprintSummary = ({ data }) => {
    const [summary, setSummary] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
      loadSummary()
    }, [])

    const loadSummary = async () => {
      try {
        const data = await wizardAPI.getEcoFootprintSummary()
        setSummary(data)
      } catch (error) {
        console.error('Error loading eco-footprint summary:', error)
      } finally {
        setLoading(false)
      }
    }

    const handleContinue = async () => {
      await wizardAPI.updateWizardProgress({ step: step, completed: true, skip: false })
      await dispatch(setUserWizardDone(step))
      await navigateAfterStepCompletion()
    }

    if (loading) {
      return (
        <StandardStepLayout data={data} buttons={[]}>
          <Box textAlign="center" py={4}>
            <Typography color="white">Chargement...</Typography>
          </Box>
        </StandardStepLayout>
      )
    }

    return (
      <StandardStepLayout
        data={data}
        buttons={[
          <Button key="continue" variant="contained" sx={getButtonStyles('primary')} onClick={handleContinue}>
            {data.text_button_1 || tLayout('wizardButtons.continue')}
          </Button>
        ]}
      >
        {summary && (
          <Box mt={4}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" color="white" mb={2} fontWeight={600}>
                      Émissions CO₂ Annuelles
                    </Typography>
                    <Typography variant="h4" color="#2ca948" mb={1}>
                      {summary.annual_co2_emissions?.toFixed(1)} t CO₂
                    </Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" mb={1}>
                      Moyenne nationale: {summary.national_average_co2?.toFixed(1)} t CO₂
                    </Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                      Objectif de réduction: {summary.reduction_goal?.toFixed(1)} t CO₂
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" color="white" mb={2} fontWeight={600}>
                      Répartition des Émissions
                    </Typography>
                    {summary.emission_distribution && Object.entries(summary.emission_distribution).map(([key, value]) => (
                      <Box key={key} mb={1}>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" display="inline">
                          {key}: 
                        </Typography>
                        <Typography variant="body2" color="white" display="inline" ml={1} fontWeight={600}>
                          {typeof value === 'number' ? value.toFixed(1) : value} t CO₂
                        </Typography>
        </Box>
                    ))}
                  </CardContent>
                </Card>
              </Grid>
              {summary.recommendation_text && (
                <Grid item xs={12}>
                  <Card sx={{ backgroundColor: 'rgba(44, 169, 72, 0.1)', border: '1px solid rgba(44, 169, 72, 0.3)' }}>
                    <CardContent>
                      <Typography variant="h6" color="white" mb={1} fontWeight={600}>
                        Recommandations
                      </Typography>
                      <Typography variant="body2" color="white">
                        {summary.recommendation_text}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              )}
            </Grid>
      </Box>
        )}
      </StandardStepLayout>
    )
  }

  // Time Management Profile Summary (Spec 4.7)
  const TimeManagementProfileSummary = ({ data }) => {
    const [summary, setSummary] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
      loadSummary()
    }, [])

    const loadSummary = async () => {
      try {
        const data = await wizardAPI.getTimeManagementProfileSummary()
        setSummary(data)
      } catch (error) {
        console.error('Error loading time management profile summary:', error)
      } finally {
        setLoading(false)
      }
    }

    const handleContinue = async () => {
      await wizardAPI.updateWizardProgress({ step: step, completed: true, skip: false })
      await dispatch(setUserWizardDone(step))
      await navigateAfterStepCompletion()
    }

    if (loading) {
      return (
        <StandardStepLayout data={data} buttons={[]}>
          <Box textAlign="center" py={4}>
            <Typography color="white">Chargement...</Typography>
          </Box>
        </StandardStepLayout>
      )
    }

    return (
      <StandardStepLayout
        data={data}
        buttons={[
          <Button key="continue" variant="contained" sx={getButtonStyles('primary')} onClick={handleContinue}>
            {data.text_button_1 || tLayout('wizardButtons.continue')}
          </Button>
        ]}
      >
        {summary && (
          <Box mt={4}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" color="white" mb={2} fontWeight={600}>
                      Personnalité Temporelle
                    </Typography>
                    <Chip 
                      label={summary.time_personality || 'Non défini'} 
                      sx={{ backgroundColor: '#0089ff', color: 'white' }}
                    />
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" color="white" mb={2} fontWeight={600}>
                      Temps Personnel
                    </Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)" mb={1}>
                      Actuel: <strong style={{ color: 'white' }}>{summary.actual_personal_time?.toFixed(1)}h</strong>
                    </Typography>
                    <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">
                      Cible: <strong style={{ color: '#2ca948' }}>{summary.personal_time_target?.toFixed(1)}h</strong>
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" color="white" mb={2} fontWeight={600}>
                      Objectifs Temporels
                    </Typography>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={4} md={3}>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">{tLayout('wizardDialogs.friendsTime', { defaultValue: 'Temps amis' })}</Typography>
                        <Typography variant="body1" color="white">{summary.friends_time_target?.toFixed(1)}h</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4} md={3}>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">Bien-être</Typography>
                        <Typography variant="body1" color="white">{summary.wellbeing_time_target?.toFixed(1)}h</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4} md={3}>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">Loisirs</Typography>
                        <Typography variant="body1" color="white">{summary.leisure_time_target?.toFixed(1)}h</Typography>
                      </Grid>
                      <Grid item xs={6} sm={4} md={3}>
                        <Typography variant="body2" color="rgba(255, 255, 255, 0.7)">{tLayout('wizardDialogs.workMax', { defaultValue: 'Professionnel max' })}</Typography>
                        <Typography variant="body1" color="white">{summary.professional_time_max?.toFixed(1)}h</Typography>
                      </Grid>
                    </Grid>
                    {summary.recommendation_text && (
                      <Box mt={3} p={2} sx={{ backgroundColor: 'rgba(0, 137, 255, 0.1)', borderRadius: 1 }}>
                        <Typography variant="body2" color="white">
                          {summary.recommendation_text}
                        </Typography>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </StandardStepLayout>
    )
  }

  // Professional Experience Summary (Spec 4.8)
  const ProfessionalExperienceSummary = ({ data }) => {
    const [summary, setSummary] = useState(null)
    const [loading, setLoading] = useState(true)

    useEffect(() => {
      loadSummary()
    }, [])

    const loadSummary = async () => {
      try {
        const data = await wizardAPI.getProfessionalExperienceSummary()
        setSummary(data)
      } catch (error) {
        console.error('Error loading professional experience summary:', error)
      } finally {
        setLoading(false)
      }
    }

    const handleContinue = async () => {
      await wizardAPI.updateWizardProgress({ step: step, completed: true, skip: false })
      await dispatch(setUserWizardDone(step))
      await navigateAfterStepCompletion()
    }

    if (loading) {
      return (
        <StandardStepLayout data={data} buttons={[]}>
          <Box textAlign="center" py={4}>
            <Typography color="white">Chargement...</Typography>
          </Box>
        </StandardStepLayout>
      )
    }

    return (
      <StandardStepLayout
        data={data}
        buttons={[
          <Button key="continue" variant="contained" sx={getButtonStyles('primary')} onClick={handleContinue}>
            {data.text_button_1 || tLayout('wizardButtons.continue')}
          </Button>
        ]}
      >
        {summary && (
          <Box mt={4}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" color="white" mb={2} fontWeight={600}>
                      Expériences Professionnelles
                    </Typography>
                    <Typography variant="h4" color="#0089ff" mb={1}>
                      {summary.total_experiences || 0}
                    </Typography>
                    {summary.experiences && summary.experiences.length > 0 && (
                      <Box mt={2}>
                        {summary.experiences.slice(0, 3).map((exp, idx) => (
                          <Box key={idx} mb={1} p={1} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: 1 }}>
                            <Typography variant="body2" color="white" fontWeight={600}>
                              {exp.title}
                            </Typography>
                            <Typography variant="caption" color="rgba(255, 255, 255, 0.7)">
                              {exp.company} • {exp.start_date} - {exp.end_date || 'Présent'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ backgroundColor: 'rgba(255, 255, 255, 0.05)', border: '1px solid rgba(255, 255, 255, 0.1)' }}>
                  <CardContent>
                    <Typography variant="h6" color="white" mb={2} fontWeight={600}>
                      Formations
                    </Typography>
                    <Typography variant="h4" color="#2ca948" mb={1}>
                      {summary.total_formations || 0}
                    </Typography>
                    {summary.formations && summary.formations.length > 0 && (
                      <Box mt={2}>
                        {summary.formations.slice(0, 3).map((form, idx) => (
                          <Box key={idx} mb={1} p={1} sx={{ backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: 1 }}>
                            <Typography variant="body2" color="white" fontWeight={600}>
                              {form.title}
                            </Typography>
                            <Typography variant="caption" color="rgba(255, 255, 255, 0.7)">
                              {form.institution} • {form.start_date} - {form.end_date || 'Présent'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </Box>
        )}
      </StandardStepLayout>
    )
  }

  // GE015 4.11.3: Domain evaluation step keys that use sliders + objective selector
  const EVALUATION_DOMAIN_STEP_KEYS = [
    'Social_Evaluation',
    'Finance_Evaluation',
    'Home_Evaluation',
    'Footprint_Evaluation',
    'Time_Evaluation',
    'Work_Evaluation',
    'Perso_Evaluation',
    'Admin_Evaluation',
  ]

  // Map wizard step key to backend domain name (Domain.model.domain)
  const STEPKEY_TO_DOMAIN = {
    Social_Evaluation: 'Social',
    Finance_Evaluation: 'Finance',
    Home_Evaluation: 'Domicile',
    Footprint_Evaluation: 'Earth',
    Time_Evaluation: 'Temps',
    Work_Evaluation: 'Professionnel',
    Perso_Evaluation: 'Personnel',
    Admin_Evaluation: 'Administratif',
  }

  // 4.11.3.4 Domain display label for title "Evaluation {label}"
  const STEPKEY_TO_DOMAIN_LABEL = {
    Social_Evaluation: 'social',
    Finance_Evaluation: 'finance',
    Home_Evaluation: 'domicile et biens',
    Footprint_Evaluation: 'empreinte carbone',
    Time_Evaluation: 'gestion du temps',
    Work_Evaluation: 'carrière professionnelle',
    Perso_Evaluation: 'vie personnelle',
    Admin_Evaluation: 'sérénité administrative',
  }

  // GE015 4.11.3.4: Exact questions per domain (importance + emotional)
  // Localised evaluation questions — keys map to translation entries in
  // wizardEvaluation.questionImportance.<key> and wizardEvaluation.questionFeeling.<key>
  const EVALUATION_STEP_KEYS = [
    'Social_Evaluation',
    'Finance_Evaluation',
    'Home_Evaluation',
    'Footprint_Evaluation',
    'Time_Evaluation',
    'Work_Evaluation',
    'Perso_Evaluation',
    'Admin_Evaluation',
  ]
  const EVALUATION_QUESTIONS = EVALUATION_STEP_KEYS.reduce((acc, key) => {
    acc[key] = {
      importance: tLayout(`wizardEvaluation.questionImportance.${key}`),
      feeling: tLayout(`wizardEvaluation.questionFeeling.${key}`),
    }
    return acc
  }, {})

  // Evaluation Step Component - GE015 4.11.3: sliders + optional objective, then Continuer/Passer
  const EvaluationStep = ({ data, stepKey }) => {
    const safeData = data || {}
    const isDomainEvaluation = EVALUATION_DOMAIN_STEP_KEYS.includes(stepKey)
    const domain = STEPKEY_TO_DOMAIN[stepKey]

    const [importanceScore, setImportanceScore] = useState(5)
    const [emotionScore, setEmotionScore] = useState(5)
    const [saving, setSaving] = useState(false)

    // Pre-fill sliders from an existing evaluation (resume). The objective
    // definition that used to live in this step was removed from the
    // onboarding: goals are now created during the Activation flow (mission
    // statement -> roadmap) or later from Mes Objectifs / quick add.
    useEffect(() => {
      if (!isDomainEvaluation || !domain) return
      let cancelled = false
      const load = async () => {
        try {
          const evaluations = await wizardAPI.getUserEvaluations()
          if (cancelled) return
          const existing = Array.isArray(evaluations) ? evaluations.find((e) => e.domain === domain) : null
          if (existing) {
            const imp = Math.min(10, Math.max(1, Number(existing.importance_score) || 5))
            const emo = Math.min(10, Math.max(1, Number(existing.emotion_score) || 5))
            setImportanceScore(imp)
            setEmotionScore(emo)
          }
        } catch (e) {
          if (!cancelled) console.error('Load evaluation:', e)
        }
      }
      load()
      return () => { cancelled = true }
    }, [stepKey, isDomainEvaluation, domain])

    const handleSkip = async () => {
      try {
        await wizardAPI.updateWizardProgress({ step: stepKey, completed: false, skip: true })
        await dispatch(setUserWizardSkip(stepKey))
        await navigateAfterStepCompletion()
      } catch (error) {
        console.error('Error skipping evaluation step:', error)
        alert(tLayout('wizardErrors.skipStep'))
      }
    }

    const handleContinue = async () => {
      try {
        setSaving(true)
        if (isDomainEvaluation && domain) {
          await wizardAPI.saveDomainEvaluation({
            domain,
            importance_score: importanceScore,
            emotion_score: emotionScore,
            objectiveList: [],
          })
          await wizardAPI.recalculatePriorities()
        }
        await wizardAPI.updateWizardProgress({ step: stepKey, completed: true, skip: false })
        await dispatch(setUserWizardDone(stepKey))
        await navigateAfterStepCompletion()
      } catch (error) {
        console.error('Error completing evaluation step:', error)
        const msg = error?.response?.data?.error || error?.message || tLayout('wizardErrors.complete')
        alert(typeof msg === 'string' ? msg : tLayout('wizardErrors.complete'))
      } finally {
        setSaving(false)
      }
    }

    const labelContinuer = safeDisplay(safeData?.text_button_1) || tLayout('wizardButtons.continue')
    const labelPasser = safeDisplay(safeData?.text_button_3) || tLayout('wizardButtons.skip')
    // Spec: Title = "Evaluation" (fixed) + domain from 4.11.3.4; logo & subtitle from current wizard
    const domainLabel = STEPKEY_TO_DOMAIN_LABEL[stepKey]
    const titleContent = isDomainEvaluation && domainLabel
      ? `Evaluation ${domainLabel}`
      : (safeDisplay(safeData?.title_content) || `Évaluation ${safeDisplay(safeData?.subtitle) || stepKey || ''}`)
    const subtitleContent = safeDisplay(safeData?.subtitle)
    const textContent = safeDisplay(safeData?.text_content)
    const messageKey = safeData?.message_key || ''

    const buttons = [
      <Button key="continue" variant="contained" sx={getButtonStyles('primary')} onClick={handleContinue} disabled={saving}>
        {labelContinuer}
      </Button>,
      <Button key="skip" variant="outlined" sx={getButtonStyles('secondary')} onClick={handleSkip} disabled={saving}>
        {labelPasser}
      </Button>,
    ]

    const layoutData = {
      ...safeData,
      title_content: titleContent,
      subtitle: subtitleContent,
      text_content: textContent || undefined,
    }

    const sliderSx = {
      color: '#0089ff',
      '& .MuiSlider-thumb': { width: 20, height: 20 },
      '& .MuiSlider-rail': { opacity: 0.5 },
    }

    return (
      <StandardStepLayout data={layoutData} buttons={buttons}>
        {isDomainEvaluation ? (
          <Box mt={4}>
            {(() => {
              const questions = EVALUATION_QUESTIONS[stepKey] || {
                importance: tLayout('wizardEvaluation.defaultImportance'),
                feeling: tLayout('wizardEvaluation.defaultFeeling'),
              }
              return (
                <>
                  {/* Question 1: Importance - GE015 4.11.3 */}
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 600, mb: 1.5 }}>
                    {questions.importance}
                  </Typography>
                  <Box sx={{ px: 0.5, mb: 3 }}>
                    <Slider
                      value={importanceScore}
                      onChange={(_, v) => setImportanceScore(v)}
                      min={1}
                      max={10}
                      step={1}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => v}
                      sx={sliderSx}
                    />
                    <Box display="flex" justifyContent="space-between" mt={0.5} sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 14 }}>
                      <span>{tLayout('wizardEvaluation.importanceLow')}</span>
                      <span>{tLayout('wizardEvaluation.importanceHigh')}</span>
                    </Box>
                  </Box>

                  {/* Question 2: Feelings - GE015 4.11.3 */}
                  <Typography variant="subtitle1" sx={{ color: 'rgba(255, 255, 255, 0.95)', fontWeight: 600, mb: 1.5 }}>
                    {questions.feeling}
                  </Typography>
                  <Box sx={{ px: 0.5, mb: 3 }}>
                    <Slider
                      value={emotionScore}
                      onChange={(_, v) => setEmotionScore(v)}
                      min={1}
                      max={10}
                      step={1}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => v}
                      sx={sliderSx}
                    />
                    <Box display="flex" justifyContent="space-between" mt={0.5} sx={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: 14 }}>
                      <span>{tLayout('wizardEvaluation.feelingLow')}</span>
                      <span>{tLayout('wizardEvaluation.feelingHigh')}</span>
                    </Box>
                  </Box>
                </>
              )
            })()}

          </Box>
        ) : (
          !textContent && (
            <Box mt={4} textAlign="center">
              <Typography variant="h6" color="rgba(255, 255, 255, 0.8)" mb={2}>
                {titleContent}
              </Typography>
              <Typography variant="body1" color="rgba(255, 255, 255, 0.6)">
                {messageKey.includes('Evaluation')
                  ? tLayout('wizardDialogs.noActionEvaluation')
                  : tLayout('wizardDialogs.noActionStep')}
              </Typography>
            </Box>
          )
        )}
      </StandardStepLayout>
    )
  }

  // 4.11.5.1 Wizard_perso : Sport – Pratique sportive
  const SportStep = ({ data }) => {
    const safeData = data || {}
    const [sportsList, setSportsList] = useState([])
    const [sportSearch, setSportSearch] = useState('')
    const [selectedSports, setSelectedSports] = useState([])
    const [selectSportOpen, setSelectSportOpen] = useState(false)
    const [sessionsOpen, setSessionsOpen] = useState(false)
    const [sessionsValues, setSessionsValues] = useState({})
    const [saving, setSaving] = useState(false)
    const [customSportName, setCustomSportName] = useState('')
    const [addingSport, setAddingSport] = useState(false)

    useEffect(() => {
      let cancelled = false
      wizardAPI.getSports().then((list) => {
        if (!cancelled) setSportsList(Array.isArray(list) ? list : [])
      }).catch((e) => { if (!cancelled) console.error(e) })
      return () => { cancelled = true }
    }, [])

    const addCustomSport = async () => {
      const name = (customSportName || '').trim()
      if (!name) return
      const norm = (n) => (n || '').toLowerCase()
      if (selectedSports.some((x) => norm(x.sport_name || x.name) === norm(name))) {
        setCustomSportName('')
        return
      }
      setAddingSport(true)
      try {
        await wizardAPI.addSport(name)
        setSportsList((prev) => {
          if (prev.some((s) => norm(s.sport_name || s.name) === norm(name))) return prev
          return [...prev, { sport_name: name, name }]
        })
        setSelectedSports((prev) => [...prev, { sport_name: name, name }])
        setCustomSportName('')
      } catch (e) {
        const custom = { sport_name: name, name }
        setSelectedSports((prev) => [...prev, custom])
        setCustomSportName('')
      } finally {
        setAddingSport(false)
      }
    }

    const filteredSports = sportsList.filter((s) => {
      const name = (s.sport_name || s.name || '').toLowerCase()
      return name.includes(sportSearch.toLowerCase().trim())
    })

    const toggleSport = (sport) => {
      const name = sport.sport_name || sport.name
      setSelectedSports((prev) =>
        prev.some((x) => (x.sport_name || x.name) === name)
          ? prev.filter((x) => (x.sport_name || x.name) !== name)
          : [...prev, { sport_name: name, name }]
      )
    }

    const handleOpenSessions = () => {
      const initial = {}
      selectedSports.forEach((s) => {
        const n = s.sport_name || s.name
        initial[n] = sessionsValues[n] ?? 0
      })
      setSessionsValues(initial)
      setSelectSportOpen(false)
      setSessionsOpen(true)
    }

    const handleSaveSports = async () => {
      const list = selectedSports.map((s) => {
        const n = s.sport_name || s.name
        return { name: n, value: Math.max(0, parseInt(sessionsValues[n], 10) || 0) }
      })
      if (list.length === 0) {
        setSessionsOpen(false)
        handleSkipSport()
        return
      }
      setSaving(true)
      try {
        await wizardAPI.saveUserSports(list)
        await wizardAPI.updateWizardProgress({ step: 'Perso_Sport', completed: true, skip: false })
        dispatch(setUserWizardDone('Perso_Sport'))
        navigateAfterStepCompletion()
      } catch (e) {
        console.error(e)
        alert(tLayout('wizardErrors.record'))
      } finally {
        setSaving(false)
      }
    }

    const handleSkipSport = async () => {
      try {
        await wizardAPI.updateWizardProgress({ step: 'Perso_Sport', completed: false, skip: true })
        dispatch(setUserWizardSkip('Perso_Sport'))
        navigateAfterStepCompletion()
      } catch (e) {
        console.error(e)
      }
    }

    const layoutData = {
      ...safeData,
      title_content: safeDisplay(safeData.title_content) || tLayout('wizardPerso.sport.title'),
      subtitle: safeDisplay(safeData.subtitle) || tLayout('wizardPerso.subtitle'),
      text_content: undefined,
    }

    return (
      <>
        <StandardStepLayout
          data={layoutData}
          buttons={[
            <Button key="skip" variant="outlined" sx={getButtonStyles('secondary')} onClick={handleSkipSport}>
              {tLayout('wizardPerso.sport.skipButton')}
            </Button>,
          ]}
        >
          <Box mt={2}>
            <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 600, mb: 1.5 }}>
              {tLayout('wizardPerso.sport.question')}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 3, lineHeight: 1.6 }}>
              {tLayout('wizardPerso.sport.description')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setSelectSportOpen(true)}
              sx={getButtonStyles('primary')}
              endIcon={<span style={{ fontSize: 18 }}>›</span>}
            >
              {tLayout('wizardPerso.sport.selectButton')}
            </Button>
          </Box>
        </StandardStepLayout>

        {/* Popup: Sélectionner sport */}
        <Dialog
          open={selectSportOpen}
          onClose={() => setSelectSportOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { bgcolor: '#1a1a2e', borderRadius: 2 } }}
        >
          <DialogTitle sx={{ color: 'white' }}>{tLayout('wizardDialogs.selectSport')}</DialogTitle>
          <DialogContent>
            <TextField
              placeholder={tLayout('wizardPerso.sport.searchPlaceholder')}
              fullWidth
              size="small"
              value={sportSearch}
              onChange={(e) => setSportSearch(e.target.value)}
              sx={{ mb: 2, '& .MuiInputBase-input': { color: 'white' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
            />
            <Box sx={{ maxHeight: 300, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {filteredSports.map((sport) => {
                const name = sport.sport_name || sport.name
                const checked = selectedSports.some((x) => (x.sport_name || x.name) === name)
                return (
                  <Box
                    key={name}
                    onClick={() => toggleSport(sport)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1,
                      cursor: 'pointer',
                      color: 'white',
                      borderBottom: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <Checkbox checked={checked} sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: '#0089ff' } }} />
                    <Typography>{name}</Typography>
                  </Box>
                )
              })}
            </Box>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, mb: 1 }}>
                {tLayout('wizardPerso.sport.addCustomTitle')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  placeholder={tLayout('wizardPerso.sport.customNamePlaceholder')}
                  size="small"
                  value={customSportName}
                  onChange={(e) => setCustomSportName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomSport() } }}
                  sx={{ flex: 1, '& .MuiInputBase-input': { color: 'white' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
                />
                <Button
                  variant="contained"
                  onClick={addCustomSport}
                  disabled={!customSportName.trim() || addingSport}
                  sx={getButtonStyles('primary')}
                >
                  {addingSport ? '...' : tLayout('wizardPerso.sport.addButton')}
                </Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleOpenSessions} disabled={selectedSports.length === 0} sx={getButtonStyles('primary')}>
              {tLayout('wizardButtons.continue')}
            </Button>
            <Button onClick={() => setSelectSportOpen(false)} sx={{ color: 'white' }}>
              {tLayout('wizardButtons.cancel')}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Popup: sessions per month for each selected sport */}
        <Dialog
          open={sessionsOpen}
          onClose={() => setSessionsOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { bgcolor: '#1a1a2e', borderRadius: 2 } }}
        >
          <DialogTitle sx={{ color: 'white' }}>{tLayout('wizardDialogs.sessionsPerMonth')}</DialogTitle>
          <DialogContent>
            {selectedSports.map((s) => {
              const name = s.sport_name || s.name
              return (
                <Box key={name} sx={{ mb: 3 }}>
                  <Typography fontWeight={600} sx={{ color: 'white', mb: 1 }}>{name}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 1 }}>
                    Combien de séances par mois souhaites-tu réaliser ?
                  </Typography>
                  <TextField
                    type="number"
                    inputProps={{ min: 0, step: 1 }}
                    value={sessionsValues[name] ?? ''}
                    onChange={(e) => setSessionsValues((prev) => ({ ...prev, [name]: e.target.value }))}
                    fullWidth
                    size="small"
                    sx={{ '& .MuiInputBase-input': { color: 'white' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
                  />
                </Box>
              )
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSaveSports} disabled={saving} sx={getButtonStyles('primary')}>
              Continuer
            </Button>
            <Button onClick={() => setSessionsOpen(false)} sx={{ color: 'white' }}>
              {tLayout('wizardButtons.cancel')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }

  // 4.11.5.2 Wizard_perso : Health – Santé
  const HealthStep = ({ data }) => {
    const safeData = data || {}
    const [preventiveFollowUp, setPreventiveFollowUp] = useState(null)
    const [medicalConsultations, setMedicalConsultations] = useState('')
    const [physicalWellbeingScore, setPhysicalWellbeingScore] = useState(5)
    const [saving, setSaving] = useState(false)

    useEffect(() => {
      let cancelled = false
      wizardAPI.getHealth().then((h) => {
        if (!cancelled && h) {
          if (typeof h.preventive_follow_up === 'boolean') setPreventiveFollowUp(h.preventive_follow_up)
          if (h.medical_consultations != null) setMedicalConsultations(String(h.medical_consultations))
          if (h.pysical_wellbeing_score != null) setPhysicalWellbeingScore(Math.min(10, Math.max(1, Number(h.pysical_wellbeing_score))) || 5)
        }
      }).catch(() => {})
      return () => { cancelled = true }
    }, [])

    const handleContinue = async () => {
      setSaving(true)
      try {
        await wizardAPI.saveUserHealth({
          preventive_follow_up: preventiveFollowUp === true,
          medical_consultations: Math.max(0, parseInt(medicalConsultations, 10) || 0),
          pysical_wellbeing_score: Math.min(10, Math.max(1, physicalWellbeingScore)),
        })
        await wizardAPI.updateWizardProgress({ step: 'Perso_Well_being', completed: true, skip: false })
        dispatch(setUserWizardDone('Perso_Well_being'))
        navigateAfterStepCompletion()
      } catch (e) {
        console.error(e)
        alert(tLayout('wizardErrors.record'))
      } finally {
        setSaving(false)
      }
    }

    const handleSkip = async () => {
      try {
        await wizardAPI.updateWizardProgress({ step: 'Perso_Well_being', completed: false, skip: true })
        dispatch(setUserWizardSkip('Perso_Well_being'))
        navigateAfterStepCompletion()
      } catch (e) {
        console.error(e)
      }
    }

    const layoutData = {
      ...safeData,
      title_content: safeDisplay(safeData.title_content) || tLayout('wizardPerso.wellbeing.title'),
      subtitle: safeDisplay(safeData.subtitle) || tLayout('wizardPerso.subtitle'),
      text_content: undefined,
    }

    const sliderSx = { color: '#0089ff', '& .MuiSlider-thumb': { width: 20, height: 20 }, '& .MuiSlider-rail': { opacity: 0.5 } }
    const inputSx = { '& .MuiInputBase-input': { color: 'white' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' }, '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' } }

    return (
      <StandardStepLayout
        data={layoutData}
        buttons={[
          <Button key="continue" variant="contained" sx={getButtonStyles('primary')} onClick={handleContinue} disabled={saving}>
            {tLayout('wizardPerso.wellbeing.continueButton')}
          </Button>,
          <Button key="skip" variant="outlined" sx={getButtonStyles('secondary')} onClick={handleSkip} disabled={saving}>
            {tLayout('wizardPerso.wellbeing.skipButton')}
          </Button>,
        ]}
      >
        <Box mt={2}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
            {tLayout('wizardPerso.wellbeing.heading')}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 3, lineHeight: 1.6 }}>
            {tLayout('wizardPerso.wellbeing.description')}
          </Typography>

          <Typography sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 600, mb: 1.5 }}>
            {tLayout('wizardPerso.wellbeing.preventiveQuestion')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
            <Button
              variant={preventiveFollowUp === true ? 'contained' : 'outlined'}
              onClick={() => setPreventiveFollowUp(true)}
              sx={preventiveFollowUp === true ? getButtonStyles('primary') : { color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
            >
              {tLayout('wizardPerso.wellbeing.yes')}
            </Button>
            <Button
              variant={preventiveFollowUp === false ? 'contained' : 'outlined'}
              onClick={() => setPreventiveFollowUp(false)}
              sx={preventiveFollowUp === false ? getButtonStyles('primary') : { color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
            >
              {tLayout('wizardPerso.wellbeing.no')}
            </Button>
          </Box>

          <Typography sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 600, mb: 1.5 }}>
            {tLayout('wizardPerso.wellbeing.consultationsQuestion')}
          </Typography>
          <TextField
            fullWidth
            type="number"
            inputProps={{ min: 0, step: 1 }}
            value={medicalConsultations}
            onChange={(e) => setMedicalConsultations(e.target.value)}
            size="small"
            sx={{ mb: 3, ...inputSx }}
          />

          <Typography sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 600, mb: 1.5 }}>
            {tLayout('wizardPerso.wellbeing.scoreQuestion')}
          </Typography>
          <Box sx={{ px: 0.5, mb: 2 }}>
            <Slider
              value={physicalWellbeingScore}
              onChange={(_, v) => setPhysicalWellbeingScore(v)}
              min={1}
              max={10}
              step={1}
              valueLabelDisplay="auto"
              valueLabelFormat={(v) => v}
              sx={sliderSx}
            />
            <Box display="flex" justifyContent="space-between" mt={0.5} sx={{ color: 'rgba(255,255,255,0.7)', fontSize: 14 }}>
              <span>{tLayout('wizardPerso.wellbeing.scoreLow')}</span>
              <span>{tLayout('wizardPerso.wellbeing.scoreHigh')}</span>
            </Box>
          </Box>
        </Box>
      </StandardStepLayout>
    )
  }

  // 4.11.5.3 Wizard_perso : Leisure – Loisirs
  const LeisureStep = ({ data }) => {
    const safeData = data || {}
    const [leisureList, setLeisureList] = useState([])
    const [leisureSearch, setLeisureSearch] = useState('')
    const [selectedLeisure, setSelectedLeisure] = useState([])
    const [selectLeisureOpen, setSelectLeisureOpen] = useState(false)
    const [percentOpen, setPercentOpen] = useState(false)
    const [percentValues, setPercentValues] = useState({})
    const [saving, setSaving] = useState(false)
    const [customLeisureName, setCustomLeisureName] = useState('')

    useEffect(() => {
      let cancelled = false
      wizardAPI.getLeisure().then((list) => {
        if (!cancelled) setLeisureList(Array.isArray(list) ? list : [])
      }).catch((e) => { if (!cancelled) console.error(e) })
      return () => { cancelled = true }
    }, [])

    const addCustomLeisure = () => {
      const name = (customLeisureName || '').trim()
      if (!name) return
      const norm = (n) => (n || '').toLowerCase()
      if (selectedLeisure.some((x) => norm(x.activity_name || x.name) === norm(name))) {
        setCustomLeisureName('')
        return
      }
      const item = { activity_name: name, name }
      setLeisureList((prev) => (prev.some((s) => norm(s.activity_name || s.name) === norm(name)) ? prev : [...prev, item]))
      setSelectedLeisure((prev) => [...prev, item])
      setCustomLeisureName('')
    }

    const filteredLeisure = leisureList.filter((s) => {
      const name = (s.activity_name || s.name || '').toLowerCase()
      return name.includes(leisureSearch.toLowerCase().trim())
    })

    const toggleLeisure = (item) => {
      const name = item.activity_name || item.name
      setSelectedLeisure((prev) =>
        prev.some((x) => (x.activity_name || x.name) === name)
          ? prev.filter((x) => (x.activity_name || x.name) !== name)
          : [...prev, { activity_name: name, name }]
      )
    }

    const handleOpenPercent = () => {
      const initial = {}
      selectedLeisure.forEach((s) => {
        const n = s.activity_name || s.name
        initial[n] = percentValues[n] ?? 0
      })
      setPercentValues(initial)
      setSelectLeisureOpen(false)
      setPercentOpen(true)
    }

    const handleSaveLeisure = async () => {
      const list = selectedLeisure.map((s) => {
        const n = s.activity_name || s.name
        return { name: n, value: Math.max(0, parseInt(percentValues[n], 10) || 0) }
      })
      if (list.length === 0) {
        setPercentOpen(false)
        handleSkipLeisure()
        return
      }
      setSaving(true)
      try {
        await wizardAPI.saveUserLeisure(list)
        await wizardAPI.updateWizardProgress({ step: 'Perso_Leisure', completed: true, skip: false })
        dispatch(setUserWizardDone('Perso_Leisure'))
        navigateAfterStepCompletion()
      } catch (e) {
        console.error(e)
        alert(tLayout('wizardErrors.record'))
      } finally {
        setSaving(false)
        setPercentOpen(false)
      }
    }

    const handleSkipLeisure = async () => {
      try {
        await wizardAPI.updateWizardProgress({ step: 'Perso_Leisure', completed: false, skip: true })
        dispatch(setUserWizardSkip('Perso_Leisure'))
        navigateAfterStepCompletion()
      } catch (e) {
        console.error(e)
      }
    }

    const layoutData = {
      ...safeData,
      title_content: safeDisplay(safeData.title_content) || tLayout('wizardPerso.leisure.title'),
      subtitle: safeDisplay(safeData.subtitle) || tLayout('wizardPerso.subtitle'),
      text_content: undefined,
    }

    return (
      <>
        <StandardStepLayout
          data={layoutData}
          buttons={[
            <Button key="skip" variant="outlined" sx={getButtonStyles('secondary')} onClick={handleSkipLeisure}>
              {tLayout('wizardPerso.leisure.skipButton')}
            </Button>,
          ]}
        >
          <Box mt={2}>
            <Typography variant="subtitle1" sx={{ color: 'rgba(255,255,255,0.95)', fontWeight: 600, mb: 1.5 }}>
              {tLayout('wizardPerso.leisure.question')}
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 3, lineHeight: 1.6 }}>
              {tLayout('wizardPerso.leisure.description')}
            </Typography>
            <Button
              variant="contained"
              onClick={() => setSelectLeisureOpen(true)}
              sx={getButtonStyles('primary')}
              endIcon={<span style={{ fontSize: 18 }}>›</span>}
            >
              {tLayout('wizardPerso.leisure.selectButton')}
            </Button>
          </Box>
        </StandardStepLayout>

        <Dialog
          open={selectLeisureOpen}
          onClose={() => setSelectLeisureOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { bgcolor: '#1a1a2e', borderRadius: 2 } }}
        >
          <DialogTitle sx={{ color: 'white' }}>{tLayout('wizardDialogs.selectLeisure')}</DialogTitle>
          <DialogContent>
            <TextField
              placeholder={tLayout('wizardPerso.leisure.searchPlaceholder')}
              fullWidth
              size="small"
              value={leisureSearch}
              onChange={(e) => setLeisureSearch(e.target.value)}
              sx={{ mb: 2, '& .MuiInputBase-input': { color: 'white' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
            />
            <Box sx={{ maxHeight: 300, overflow: 'auto', display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {filteredLeisure.map((item) => {
                const name = item.activity_name || item.name
                const checked = selectedLeisure.some((x) => (x.activity_name || x.name) === name)
                return (
                  <Box
                    key={name}
                    onClick={() => toggleLeisure(item)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      py: 1,
                      cursor: 'pointer',
                      color: 'white',
                      borderBottom: '1px solid rgba(255,255,255,0.08)',
                    }}
                  >
                    <Checkbox checked={checked} sx={{ color: 'rgba(255,255,255,0.7)', '&.Mui-checked': { color: '#0089ff' } }} />
                    <Typography>{name}</Typography>
                  </Box>
                )
              })}
            </Box>
            <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid rgba(255,255,255,0.15)' }}>
              <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: 14, mb: 1 }}>
                {tLayout('wizardPerso.leisure.addCustomTitle')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  placeholder={tLayout('wizardPerso.leisure.customNamePlaceholder')}
                  size="small"
                  value={customLeisureName}
                  onChange={(e) => setCustomLeisureName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomLeisure() } }}
                  sx={{ flex: 1, '& .MuiInputBase-input': { color: 'white' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
                />
                <Button variant="contained" onClick={addCustomLeisure} disabled={!customLeisureName.trim()} sx={getButtonStyles('primary')}>
                  {tLayout('wizardPerso.leisure.addButton')}
                </Button>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleOpenPercent} disabled={selectedLeisure.length === 0} sx={getButtonStyles('primary')}>
              {tLayout('wizardButtons.continue')}
            </Button>
            <Button onClick={() => setSelectLeisureOpen(false)} sx={{ color: 'white' }}>
              {tLayout('wizardButtons.cancel')}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog
          open={percentOpen}
          onClose={() => setPercentOpen(false)}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { bgcolor: '#1a1a2e', borderRadius: 2 } }}
        >
          <DialogTitle sx={{ color: 'white' }}>{tLayout('wizardDialogs.desiredTimePerActivity')}</DialogTitle>
          <DialogContent>
            <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 2 }}>
              {tLayout('wizardPerso.leisure.percentDescription')}
            </Typography>
            {selectedLeisure.map((s) => {
              const name = s.activity_name || s.name
              return (
                <Box key={name} sx={{ mb: 3 }}>
                  <Typography fontWeight={600} sx={{ color: 'white', mb: 1 }}>{name}</Typography>
                  <TextField
                    type="number"
                    inputProps={{ min: 0, max: 100, step: 1 }}
                    value={percentValues[name] ?? ''}
                    onChange={(e) => setPercentValues((prev) => ({ ...prev, [name]: e.target.value }))}
                    fullWidth
                    size="small"
                    placeholder={tLayout('wizardPerso.leisure.percentPlaceholder')}
                    sx={{ '& .MuiInputBase-input': { color: 'white' }, '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(255,255,255,0.3)' } }}
                  />
                </Box>
              )
            })}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleSaveLeisure} disabled={saving} sx={getButtonStyles('primary')}>
              {tLayout('wizardButtons.continue')}
            </Button>
            <Button onClick={() => setPercentOpen(false)} sx={{ color: 'white' }}>
              {tLayout('wizardButtons.cancel')}
            </Button>
          </DialogActions>
        </Dialog>
      </>
    )
  }

  const END_SCREEN_IMAGE_FALLBACK = getUrl() + '/media/Guide/fusee.png'

  const Endwelcome = ({ data, onDiscoverPortrait }) => {
    const { t } = useTranslation('layout')
    const safeData = data || {}
    const prenom = siteUser.user?.first_name || ''
    // Prefer API logo (S3 URL in staging); backend /media/ returns 404 when media is on S3
    const logoSrc = (data?.logo && (data.logo.startsWith('http') || data.logo.startsWith('/')))
      ? (data.logo.startsWith('http') ? data.logo : getUrl() + data.logo)
      : END_SCREEN_IMAGE_FALLBACK
    return (
      <Box
        color="white"
        display="flex"
        flexDirection={{ xs: 'column', lg: 'row' }}
        pt={0}
        sx={{ position: 'relative', zIndex: 1 }}
      >
        <Box width={{ xs: '100%', lg: '70%' }} px={{ xs: 2, md: 5 }}>
          <Box
            display="flex"
            justifyContent="center"
            flexDirection="column"
            alignItems="center"
            color="white"
            minHeight="70vh"
            textAlign="center"
          >
            {/* 1. Icon + 2. Title: rocket (light blue circle) and "Prêt pour ton voyage" on same row */}
            <Box
              display="flex"
              flexDirection={{ xs: 'column', sm: 'row' }}
              alignItems="center"
              justifyContent="center"
              gap={{ xs: 1.5, sm: 2 }}
            >
              <Box
                sx={{
                  width: { xs: 100, md: 120 },
                  height: { xs: 100, md: 120 },
                  borderRadius: '50%',
                  // backgroundColor: 'rgba(0, 137, 255, 0.35)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  '& img': {
                    height: { xs: '64px', md: '80px' },
                    width: 'auto',
                    objectFit: 'contain',
                  },
                }}
              >
                <img
                  src={logoSrc}
                  alt=""
                  onError={(e) => {
                    e.target.onerror = null
                    // If S3 logo failed, try backend; if backend failed, try S3
                    const nextSrc = logoSrc === END_SCREEN_IMAGE_FALLBACK && data?.logo
                      ? (data.logo.startsWith('http') ? data.logo : getUrl() + data.logo)
                      : END_SCREEN_IMAGE_FALLBACK
                    if (nextSrc !== logoSrc) e.target.src = nextSrc
                    e.target.alt = ''
                  }}
                />
              </Box>
              <Box
                fontSize={{ xs: 22, sm: 26, md: 30 }}
                fontWeight={700}
                color="white"
                textAlign={{ xs: 'center', sm: 'left' }}
              >
                {t('endwelcome.title')}
              </Box>
            </Box>
            {/* 3. Text: greeting + fixed lines (i18n-driven) */}
            <Box
              mt={4}
              fontSize={{ xs: 15, md: 17 }}
              lineHeight={1.75}
              maxWidth="640px"
              color="rgba(255, 255, 255, 0.95)"
              px={{ xs: 1, md: 0 }}
            >
              {t('endwelcome.greeting', { name: prenom || t('endwelcome.defaultName') })}<br /><br />
              {t('endwelcome.line1')}<br /><br />
              {t('endwelcome.line2')}<br /><br />
              {t('endwelcome.line3')}
            </Box>
            {/* 4. Découvrir ton portrait | 5. Passer */}
            <Box mt={5} display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center" justifyContent="center">
              <Button
                variant="contained"
                onClick={onDiscoverPortrait || handleDiscoverPortrait}
                sx={{
                  minWidth: { xs: '100%', sm: '260px' },
                  py: 1.5,
                  px: 3,
                  fontSize: 16,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  backgroundColor: '#0089ff',
                  color: 'white',
                  '&:hover': { backgroundColor: '#0070cc' },
                }}
              >
                {t('endwelcome.discoverPortrait')}
              </Button>
              <Button
                variant="outlined"
                onClick={handelFinsh}
                sx={{
                  minWidth: { xs: '100%', sm: '140px' },
                  py: 1.5,
                  px: 3,
                  fontSize: 16,
                  fontWeight: 600,
                  textTransform: 'none',
                  borderRadius: 2,
                  color: 'white',
                  borderColor: 'rgba(255, 255, 255, 0.6)',
                  '&:hover': {
                    borderColor: 'rgba(255, 255, 255, 0.9)',
                    backgroundColor: 'rgba(255, 255, 255, 0.08)',
                  },
                }}
              >
                {t('endwelcome.skip')}
              </Button>
            </Box>
          </Box>
        </Box>
        <ProgConfig data={wizard} />
      </Box>
    )
  }

  // Domain name → icon for portrait (Tes priorités, objectifs)
  const DOMAIN_ICONS = {
    Administratif: '🗂️',
    Personnel: '👤',
    Domicile: '🏠',
    Spirituel: '🧘',
    'Image de soi': '🪞',
    Santé: '🏥',
    Social: '🧑‍🤝‍🧑',
    'Earth (Planète)': '🌍',
    Earth: '🌍',
    Amis: '🤝',
    Famille: '👨‍👩‍👧‍👦',
    Loisirs: '🎨',
    Sport: '🏋️',
    Professionnel: '💼',
    Temps: '⏳',
    Finance: '📈',
  }

  // 4.13.1.2 User portrait – one scrollable page (point de départ, priorités, objectifs, équilibre temps, archétype)
  const UserPortrait = ({ onGoToLife }) => {
    const { t } = useTranslation('layout')
    const [loading, setLoading] = useState(true)
    const [portrait, setPortrait] = useState(null)
    const [timeBreakdown, setTimeBreakdown] = useState([])
    const prenom = siteUser.user?.first_name || ''

    useEffect(() => {
      let cancelled = false
      Promise.allSettled([
        wizardAPI.getTextCombination().catch((e) => { console.error('getTextCombination:', e); return null }),
        wizardAPI.getTimeBreakdown().catch((e) => { console.error('getTimeBreakdown:', e); return [] }),
      ]).then(([textResult, breakdownResult]) => {
        if (cancelled) return
        setPortrait(textResult.status === 'fulfilled' && textResult.value ? textResult.value : null)
        const breakdown = breakdownResult.status === 'fulfilled' && Array.isArray(breakdownResult.value) ? breakdownResult.value : []
        setTimeBreakdown(breakdown)
      }).finally(() => { if (!cancelled) setLoading(false) })
      return () => { cancelled = true }
    }, [])

    if (loading) {
      return (
        <Box color="white" display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" px={2}>
          <Typography>{t('mirror.loading')}</Typography>
        </Box>
      )
    }

    const tc = portrait?.text_combination || []
    const userFinance = portrait?.user_finance_profile || ''
    const userTime = portrait?.user_time_profile || ''
    const combination = tc.find((r) => (r.finance_profile || '') === userFinance && (r.time_profile || '') === userTime) || tc[0] || {}
    const domains = portrait?.domains || []
    const timeText = portrait?.user_timebreack_text || ''
    const hasNoPortraitData = !portrait || (domains.length === 0 && timeBreakdown.every((i) => !(parseFloat(i.current_hours) || 0) && !(parseFloat(i.desired_hours) || 0)))
    const getDomainIcon = (d) => DOMAIN_ICONS[d.name] || d.logo || '📌'

    const totalCurrent = timeBreakdown.reduce((s, i) => s + (parseFloat(i.current_hours) || 0), 0)
    const donutCurrent = timeBreakdown
      .map((item, idx) => ({
        name: item.domain,
        value: totalCurrent > 0 ? Math.round(((parseFloat(item.current_hours) || 0) / 168) * 100) : 0,
        color: TIME_DONUT_COLORS[idx % TIME_DONUT_COLORS.length],
      }))
      .filter((d) => d.value > 0)
    const donutDesired = timeBreakdown
      .map((item, idx) => ({
        name: item.domain,
        value: Math.round(((parseFloat(item.desired_hours) || 0) / 168) * 100),
        color: TIME_DONUT_COLORS[idx % TIME_DONUT_COLORS.length],
      }))
      .filter((d) => d.value > 0)

    const renderDonut = (pieData, title) => (
      <Box sx={{ flex: 1, minWidth: 160 }}>
        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1, textAlign: 'center' }}>{title}</Typography>
        <ResponsiveContainer width="100%" height={180}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={45}
              outerRadius={70}
              paddingAngle={2}
              label={({ name, value }) => (value > 0 ? `${name} ${value}%` : '')}
            >
              {pieData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4 }} formatter={(v, n) => [`${v}%`, n]} />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    )

    const sectionTitle = (icon, text) => (
      <Typography variant="subtitle1" fontWeight={700} sx={{ color: 'white', mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <span style={{ fontSize: 24 }}>{icon}</span>
        {text}
      </Typography>
    )

    // LAP-736: derive whether each section has content. Empty sections must
    // never render — they create the impression of a broken feature.
    const hasStartingPoint = !!(userFinance || userTime || combination.starting_point_text)
    const topDomains = domains.slice(0, 3)
    const hasPriorities = topDomains.length > 0
    const objectives = domains.slice(0, 3).filter((d) => d.objective)
    const hasObjectives = objectives.length > 0
    const hasTimeBalance = donutCurrent.length > 0 || donutDesired.length > 0 || !!timeText
    const hasArchetype = !!(combination.life_archetype_name || combination.life_archetype_text)

    // LAP-736: when the user has nothing in their portrait, render the helper
    // text and the CTA only. Otherwise hide that helper.
    return (
      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          px: { xs: 2, md: 4 },
          pb: 6,
          pt: 2,
        }}
      >
        <Typography variant="h4" fontWeight={700} sx={{ color: 'white', mb: 4, textAlign: 'center' }}>
          {t('mirror.title')}
        </Typography>
        {hasNoPortraitData && (
          <Typography sx={{ color: 'rgba(255,255,255,0.8)', mb: 3, textAlign: 'center', fontStyle: 'italic' }}>
            {t('mirror.emptyState')}
          </Typography>
        )}

        {/* 1. Ton point de départ */}
        {hasStartingPoint && (
          <Box sx={{ mb: 5 }}>
            {sectionTitle('🧭', t('mirror.startingPoint'))}
            {(userFinance || userTime) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                {userFinance && (
                  <Box sx={{ flex: 1, minWidth: 140, p: 2.5, borderRadius: 2, border: '1px solid rgba(255,255,255,0.15)', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 32, mb: 1, opacity: 0.9 }}>📈</Typography>
                    <Typography fontWeight={700} sx={{ color: 'white', fontSize: 18 }}>{userFinance}</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>{t('mirror.financeProfile')}</Typography>
                  </Box>
                )}
                {userTime && (
                  <Box sx={{ flex: 1, minWidth: 140, p: 2.5, borderRadius: 2, border: '1px solid rgba(255,255,255,0.15)', textAlign: 'center' }}>
                    <Typography sx={{ fontSize: 32, mb: 1, opacity: 0.9 }}>🎯</Typography>
                    <Typography fontWeight={700} sx={{ color: 'white', fontSize: 18 }}>{userTime}</Typography>
                    <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)', mt: 0.5 }}>{t('mirror.timeProfile')}</Typography>
                  </Box>
                )}
              </Box>
            )}
            {combination.starting_point_text && (
              <Typography sx={{ color: 'rgba(255,255,255,0.95)', lineHeight: 1.7, textAlign: 'center' }}>
                {t('mirror.personalPrefix', { name: prenom || t('mirror.defaultName') })}, {combination.starting_point_text}
              </Typography>
            )}
          </Box>
        )}

        {/* 2. Ton archétype — LAP-736: promoted right after "point de départ"
            because it is the strongest emotional hook of the portrait. */}
        {hasArchetype && (
          <Box sx={{ mb: 5 }}>
            {sectionTitle('🧬', combination.life_archetype_name ? t('mirror.archetypeWithName', { name: combination.life_archetype_name }) : t('mirror.archetype'))}
            {combination.life_archetype_text && (
              <Typography sx={{ color: 'rgba(255,255,255,0.95)', lineHeight: 1.7 }}>
                {combination.life_archetype_text}
              </Typography>
            )}
          </Box>
        )}

        {/* 3. Tes priorités */}
        {hasPriorities && (
          <Box sx={{ mb: 5 }}>
            {sectionTitle('📋', t('mirror.priorities'))}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center' }}>
              {topDomains.map((d) => (
                <Box
                  key={d.id || d.name}
                  sx={{
                    flex: '1 1 120px',
                    maxWidth: 200,
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid rgba(255,255,255,0.15)',
                    textAlign: 'center',
                    bgcolor: 'rgba(255,255,255,0.04)',
                  }}
                >
                  <Typography sx={{ fontSize: 36, mb: 1 }}>{getDomainIcon(d)}</Typography>
                  <Typography fontWeight={600} sx={{ color: 'white', fontSize: 15 }}>{d.name}</Typography>
                </Box>
              ))}
            </Box>
            <Typography sx={{ color: 'rgba(255,255,255,0.9)', mt: 2, lineHeight: 1.7, textAlign: 'center' }}>
              {t('mirror.prioritiesHelper')}
            </Typography>
          </Box>
        )}

        {/* 4. Tes premiers objectifs activés — LAP-736: replace the empty-state
            text with a configure CTA, or hide entirely when no objectives. */}
        {hasObjectives ? (
          <Box sx={{ mb: 5 }}>
            {sectionTitle('🎯', t('mirror.objectives'))}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {objectives.map((d, i) => (
                <Box
                  key={i}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2,
                    p: 2,
                    borderRadius: 2,
                    bgcolor: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                  }}
                >
                  <Typography sx={{ fontSize: 28 }}>{getDomainIcon(d)}</Typography>
                  <Typography sx={{ color: 'rgba(255,255,255,0.95)', fontSize: 16 }}>{d.objective}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        ) : null}

        {/* 5. Ton équilibre de temps — LAP-736: hide entirely if no donut data
            AND no narrative text, instead of showing an empty header. */}
        {hasTimeBalance && (
          <Box sx={{ mb: 5 }}>
            {sectionTitle('⏳', t('mirror.timeBalance'))}
            {(donutCurrent.length > 0 || donutDesired.length > 0) && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, justifyContent: 'center', mb: 2 }}>
                {donutCurrent.length > 0 && renderDonut(donutCurrent, t('mirror.current'))}
                {donutDesired.length > 0 && renderDonut(donutDesired, t('mirror.desired'))}
              </Box>
            )}
            {timeText && (
              <Typography sx={{ color: 'rgba(255,255,255,0.95)', lineHeight: 1.7, textAlign: 'center' }}>
                {timeText}
              </Typography>
            )}
          </Box>
        )}

        {/* 6. Message de conclusion */}
        <Box sx={{ mb: 5, textAlign: 'center' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.98)', lineHeight: 1.8, fontSize: 17 }}>
            {t('mirror.conclusion.line1', { name: prenom || t('mirror.defaultName') })}<br />
            {t('mirror.conclusion.line2')}<br />
            {t('mirror.conclusion.line3')}<br />
            {t('mirror.conclusion.line4')}<br /><br />
            {t('mirror.conclusion.line5')}
          </Typography>
        </Box>

        {/* 7. CTA — LAP-736: lands on "/" which renders the SmartFeed via
            HomePage when the wizard is complete. The handler navigates first
            and runs cleanup async (see handelFinsh) for an instant transition. */}
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={onGoToLife}
            sx={{
              ...getButtonStyles('primary'),
              minWidth: 280,
              py: 1.5,
              px: 4,
              fontSize: 17,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
            }}
          >
            {t('mirror.cta')}
          </Button>
        </Box>
      </Box>
    )
  }

  // GE015 v1.6 §3 — Wheel of Life
  // Displayed once between the Mirror portrait (UserPortrait) and the
  // landing on the SmartFeed. Renders a radar chart over the 8 Life Resume
  // domains.
  // LAP-752 — switched from `/api/general/text-combination/` (which read raw
  // UserDomainEvaluation rows and often came back empty during onboarding)
  // to `/api/general/life-resume/radar/?type=ONBOARDING` so the wheel uses
  // the exact same data source as the Life Resume page. The ONBOARDING
  // snapshot is regenerated before fetching so the freshest evaluations are
  // included.
  // The CTA is debounced (single click → loading state → handelFinsh) so
  // rapid double-clicks cannot trigger duplicate finalisations.
  const WheelOfLife = ({ onContinue }) => {
    const { t } = useTranslation('layout')
    const [loading, setLoading] = useState(true)
    const [wheelData, setWheelData] = useState([])
    const [submitting, setSubmitting] = useState(false)

    useEffect(() => {
      let cancelled = false
      const loadWheel = async () => {
        try {
          // 1. Refresh the ONBOARDING snapshot so its domain_scores reflect
          //    every evaluation the user just submitted. Best-effort: a 4xx
          //    or 5xx here doesn't block the radar from rendering.
          await wizardAPI.generateOnboardingSnapshot()

          // 2. Fetch the Life Resume radar (same payload Life Resume uses).
          const radar = await wizardAPI.getLifeResumeRadar({ type: 'ONBOARDING', limit: 1 })
          if (cancelled) return

          const domains = Array.isArray(radar?.domains) ? radar.domains : []
          const series = Array.isArray(radar?.series) ? radar.series : []
          const scores = (series[0] && Array.isArray(series[0].scores)) ? series[0].scores : []

          const mapped = domains.map((domainCode, idx) => {
            const raw = scores[idx]
            const num = typeof raw === 'number' ? raw : null
            return {
              domain: domainCode,
              label: t(`wheelOfLife.domains.${domainCode}`, { defaultValue: domainCode }),
              score: num !== null ? num : 0,
              noData: num === null,
            }
          })
          setWheelData(mapped)
        } catch (err) {
          console.error('WheelOfLife: failed to load wheel data', err)
          // Fallback: try the legacy endpoint so users with an existing
          // text-combination payload still see something.
          try {
            const data = await wizardAPI.getTextCombination()
            if (cancelled) return
            const raw = Array.isArray(data?.wheel_of_life) ? data.wheel_of_life : []
            const mapped = raw.map((entry) => ({
              domain: entry.domain,
              label: t(`wheelOfLife.domains.${entry.domain}`, { defaultValue: entry.domain }),
              score: typeof entry.score === 'number' ? entry.score : 0,
              noData: Boolean(entry.no_data),
            }))
            setWheelData(mapped)
          } catch (err2) {
            console.error('WheelOfLife: fallback fetch also failed', err2)
          }
        } finally {
          if (!cancelled) setLoading(false)
        }
      }
      loadWheel()
      return () => {
        cancelled = true
      }
    }, [t])

    const handleClick = () => {
      // GE015 v1.6 §2 — anti-double-click: disable the button while the
      // finalisation runs so the user can't trigger handelFinsh twice.
      if (submitting) return
      setSubmitting(true)
      try {
        onContinue?.()
      } finally {
        // Reset is mostly cosmetic — navigation away unmounts the component.
        // We still keep the guard for the unlikely case where the navigation
        // is cancelled (e.g. confirm dialog) and the user is still on screen.
        setTimeout(() => setSubmitting(false), 1500)
      }
    }

    if (loading) {
      return (
        <Box color="white" display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" px={2}>
          <Typography>{t('mirror.loading')}</Typography>
        </Box>
      )
    }

    const hasAnyData = wheelData.some((d) => !d.noData)

    return (
      <Box color="white" px={{ xs: 2, sm: 4 }} py={4} maxWidth={720} mx="auto">
        <Typography
          variant="h5"
          fontWeight={700}
          sx={{ color: 'white', textAlign: 'center', mb: 2 }}
        >
          {t('wheelOfLife.title')}
        </Typography>

        <Typography sx={{ color: 'rgba(255,255,255,0.92)', textAlign: 'center', mb: 4, lineHeight: 1.6 }}>
          {t('wheelOfLife.intro')}
        </Typography>

        <Box sx={{ width: '100%', maxWidth: 480, mx: 'auto', mb: 3 }}>
          <ResponsiveContainer width="100%" height={360}>
            <RadarChart data={wheelData} outerRadius="75%">
              <PolarGrid stroke="rgba(255,255,255,0.25)" />
              <PolarAngleAxis
                dataKey="label"
                tick={{ fill: 'rgba(255,255,255,0.92)', fontSize: 12 }}
              />
              <PolarRadiusAxis
                domain={[0, 10]}
                tickCount={6}
                stroke="rgba(255,255,255,0.25)"
                tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 10 }}
                angle={90}
              />
              <Radar
                name={t('wheelOfLife.legend.score')}
                dataKey="score"
                stroke="#1F3864"
                fill="#1F3864"
                fillOpacity={0.45}
                strokeWidth={2}
                isAnimationActive
              />
              <Tooltip
                contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4 }}
                formatter={(value, name, ctx) => {
                  const item = ctx?.payload?.payload
                  if (item?.noData) return [t('wheelOfLife.legend.noData'), item.label]
                  return [`${value}/10`, item?.label || name]
                }}
              />
            </RadarChart>
          </ResponsiveContainer>
        </Box>

        {!hasAnyData && (
          <Typography sx={{ color: 'rgba(255,255,255,0.7)', textAlign: 'center', mb: 3, fontStyle: 'italic' }}>
            {t('wheelOfLife.legend.noData')}
          </Typography>
        )}

        <Typography sx={{ color: 'rgba(255,255,255,0.92)', textAlign: 'center', mb: 4, lineHeight: 1.6 }}>
          {t('wheelOfLife.commitment')}
        </Typography>

        {/* LAP-752 — remind users they can revisit this view in Life Resume */}
        <Typography
          sx={{
            color: 'rgba(255,255,255,0.7)',
            textAlign: 'center',
            mb: 4,
            lineHeight: 1.5,
            fontSize: 14,
            fontStyle: 'italic',
          }}
        >
          {t('wheelOfLife.lifeResumeNote')}
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={handleClick}
            disabled={submitting}
            sx={{
              ...getButtonStyles('primary'),
              minWidth: 280,
              py: 1.5,
              px: 4,
              fontSize: 17,
              fontWeight: 700,
              textTransform: 'none',
              borderRadius: 2,
            }}
          >
            {t('wheelOfLife.cta')}
          </Button>
        </Box>
      </Box>
    )
  }

  const handelAddContact = () => {
    localStorage.setItem('w-contact', step)
    navigate('/MesContacts')
    setTimeout(() => {
      if (step === 'Social_Contact') {
        fetchContactCount()
      }
    }, 1000)
  }
  const handelImportContact = () => {
    localStorage.setItem('w-contact', step)
    navigate('/MesContacts')
    setTimeout(() => {
      if (step === 'Social_Contact') {
        fetchContactCount()
      }
    }, 1000)
  }
  const SocialContact = ({ data }) => {
    const statusMessage = contactCount > 0 
      ? `${contactCount} contact${contactCount > 1 ? 's' : ''} ajouté${contactCount > 1 ? 's' : ''} à votre cercle`
      : null

    return (
      <StandardStepLayout 
        data={data} 
        statusMessage={statusMessage}
        buttons={[
            <Button
            key="add"
              variant="contained"
              sx={{
              backgroundColor: '#0089ff',
              background: 'linear-gradient(135deg, #0089ff 0%, #00a8ff 100%)',
              color: 'white',
              fontWeight: 600,
              '&:hover': { 
                backgroundColor: '#0070cc',
                background: 'linear-gradient(135deg, #0070cc 0%, #0089ff 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0, 137, 255, 0.4)',
              }
            }} 
            onClick={handelAddContact}
          >
            {data.text_button_1}
          </Button>,
          <Button
            key="import"
            type="button"
            variant="outlined"
            sx={{
              color: 'white',
              borderColor: 'rgba(255, 255, 255, 0.4)',
              borderWidth: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.05)',
              fontWeight: 600,
              backdropFilter: 'blur(10px)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.8)',
                backgroundColor: 'rgba(255, 255, 255, 0.15)',
                transform: 'translateY(-2px)',
                boxShadow: '0 4px 12px rgba(255, 255, 255, 0.2)',
              },
            }}
            onClick={handelImportContact}
          >
            {data?.text_button_2 || 'Importer contact'}
          </Button>,
          <Button
            key="valider"
            variant="contained"
            sx={getButtonStyles('primary')}
            onClick={handleValidateStep}
          >
            {tLayout('wizardButtons.validate')}
          </Button>,
          <Button 
            key="skip"
            variant="contained" 
            sx={getButtonStyles('primary')} 
            onClick={handelSkip}
          >
              {data.text_button_3 || tLayout('wizardButtons.skip')}
            </Button>
        ]}
      />
    )
  }
  const handelAddAccout = () => {
    localStorage.setItem('w-account', step)
    navigate('/mescomptes')
    // Refresh account count when returning
    setTimeout(() => {
      if (step === 'Finance_Account') {
        fetchAccountCount()
      }
    }, 1000)
  }
  const FinanceAccount = ({ data }) => {
    const statusMessage = accountCount > 0 
      ? `${accountCount} Compte${accountCount > 1 ? 's' : ''} ajouté${accountCount > 1 ? 's' : ''}`
      : null

    return (
      <StandardStepLayout 
        data={data}
        statusMessage={statusMessage}
        buttons={[
          <Button 
            key="add"
            variant="contained" 
            sx={{ 
              backgroundColor: '#0089ff',
              '&:hover': { backgroundColor: '#0070cc' }
            }} 
            onClick={handelAddAccout}
          >
              {data.text_button_1}
          </Button>,
          <Button 
            key="valider"
            variant="contained" 
            sx={getButtonStyles('primary')} 
            onClick={handleValidateStep}
          >
            {tLayout('wizardButtons.validate')}
          </Button>,
          <Button 
            key="skip"
            variant="contained" 
            sx={getButtonStyles('primary')} 
            onClick={handelSkip}
          >
              {data.text_button_2 || tLayout('wizardButtons.skip')}
            </Button>
        ]}
      />
    )
  }
  const handelTFKnowledge = () => {
    localStorage.setItem('w-tfknowledge', step)
    navigate('/MonPatrimoine')
  }
  const Financeknowledge = ({ data }) => (
    <StandardStepLayout 
      data={data}
      buttons={[
        <Button key="action" variant="contained" sx={getButtonStyles('primary')} onClick={handelTFKnowledge}>
              {data.text_button_1}
        </Button>,
        <Button key="valider" variant="contained" sx={getButtonStyles('primary')} onClick={handleValidateStep}>
          {tLayout('wizardButtons.validate')}
        </Button>,
        <Button key="skip" variant="contained" sx={getButtonStyles('primary')} onClick={handelSkip}>
              {data.text_button_2}
            </Button>
      ]}
    />
  )
  const handelFBudget = () => {
    localStorage.setItem('w-monbuget', step)
    navigate('/Monbudget')
  }
  const Financebudget = ({ data }) => (
    <StandardStepLayout 
      data={data}
      buttons={[
        <Button key="action" variant="contained" sx={getButtonStyles('primary')} onClick={handelFBudget}>
              {data.text_button_1}
        </Button>,
        <Button key="valider" variant="contained" sx={getButtonStyles('primary')} onClick={handleValidateStep}>
          {tLayout('wizardButtons.validate')}
        </Button>,
        <Button key="skip" variant="contained" sx={getButtonStyles('primary')} onClick={handelSkip}>
              {data.text_button_2}
            </Button>
      ]}
    />
  )
  const handelFInterest = () => {
    localStorage.setItem('w-finterest', step)
    navigate('/MonPatrimoine')
  }
  const FinanceInterest = ({ data }) => (
    <StandardStepLayout 
      data={data}
      buttons={[
        <Button key="action" variant="contained" sx={getButtonStyles('primary')} onClick={handelFInterest}>
              {data.text_button_1}
        </Button>,
        <Button key="valider" variant="contained" sx={getButtonStyles('primary')} onClick={handleValidateStep}>
          {tLayout('wizardButtons.validate')}
        </Button>,
        <Button key="skip" variant="contained" sx={getButtonStyles('primary')} onClick={handelSkip}>
              {data.text_button_2}
            </Button>
      ]}
    />
  )
  const handelFProfile = () => {
    sessionStorage.setItem('w-fprofile', step)
    navigate('/MonPatrimoine')
  }
  const FinanceProfile = ({ data }) => {
    const [fpResultOpen, setFpResultOpen] = useState(false)
    const [fpResultData, setFpResultData] = useState(null)

    useEffect(() => {
      const profileName = sessionStorage.getItem('w-fprofile-result')
      if (!profileName) return
      // LAP-752 — backend may return the profile name in the user's language
      // (e.g. 'Cautious' for EN). Reverse-lookup to the FR canonical key
      // before matching against FINANCE_PROFILE_OUTPUT.
      const canonical = findCanonicalProfileName(profileName, 'financeProfiles')
      const match = FINANCE_PROFILE_OUTPUT.find((p) => p.name === canonical)
      if (match) {
        setFpResultData(match)
        // Persist the canonical FR name so the Mirror screen lookup keeps working.
        localStorage.setItem('user-fprofile', canonical)
        setFpResultOpen(true)
      }
      sessionStorage.removeItem('w-fprofile-result')
    }, [])

    const handleCloseFpResult = async () => {
      setFpResultOpen(false)
      await handleValidateStep()
    }

    return (
      <>
        <Dialog
          open={fpResultOpen}
          onClose={handleCloseFpResult}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { bgcolor: '#1a1a2e', borderRadius: 2 } }}
        >
          <DialogContent>
            {fpResultData && (
              <Box color="white" p={1}>
                <Typography sx={{ fontSize: 20, fontWeight: 500, textAlign: 'center' }}>
                  Votre profil financier est :
                </Typography>
                <Typography sx={{ fontSize: 25, fontWeight: 700, textAlign: 'center', color: '#1d629e', mt: 1 }}>
                  {fpResultData.name}
                </Typography>
                <Box display="flex" justifyContent="center" my={2} sx={{ img: { maxHeight: 140 } }}>
                  <img src={fpResultData.logo} alt={fpResultData.name} />
                </Box>
                <Typography sx={{ textAlign: 'center', lineHeight: 1.6 }}>
                  {fpResultData.text}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button onClick={handleCloseFpResult} variant="contained" sx={getButtonStyles('primary')}>
              Continuer
            </Button>
          </DialogActions>
        </Dialog>
        <StandardStepLayout
          data={data}
          buttons={[
            <Button key="action" variant="contained" sx={getButtonStyles('primary')} onClick={handelFProfile}>
              {data.text_button_1}
            </Button>,
            <Button key="valider" variant="contained" sx={getButtonStyles('primary')} onClick={handleValidateStep}>
              {tLayout('wizardButtons.validate')}
            </Button>,
            <Button key="skip" variant="contained" sx={getButtonStyles('primary')} onClick={handelSkip}>
              {data.text_button_2}
            </Button>,
          ]}
        />
      </>
    )
  }
  const handelLogement = () => {
    localStorage.setItem('w-logement', step)
    navigate('/domicile/monimmobilier')
  }
  const handelVehicule = () => {
    localStorage.setItem('w-vehicule', step)
    navigate('/domicile/mesvehicules')
  }
  const HomeREVH = ({ data }) => (
    <StandardStepLayout 
      data={data}
      buttons={[
            <Button
          key="logement"
              variant="contained"
              startIcon={wl_done && <FileDownloadDoneIcon />}
          sx={getButtonStyles('primary')}
              onClick={handelLogement}
            >
              {data.text_button_1}
        </Button>,
            <Button
          key="vehicule"
              variant="contained"
              startIcon={wv_done && <FileDownloadDoneIcon />}
          sx={getButtonStyles('primary')}
              onClick={handelVehicule}
            >
              {data.text_button_2}
        </Button>,
            <Button key="valider" variant="contained" sx={getButtonStyles('primary')} onClick={handleValidateStep}>
              {tLayout('wizardButtons.validate')}
            </Button>,
            <Button
          key="skip"
              variant="contained"
          sx={getButtonStyles('primary')}
              onClick={() => {
                handelSkip()
                localStorage.removeItem('w-logement')
                localStorage.removeItem('w-vehicule')
                localStorage.removeItem('wl_done')
                localStorage.removeItem('wv_done')
              }}
            >
              {data.text_button_3}
            </Button>
      ]}
    />
  )
  const handelFoodPrint = () => {
    sessionStorage.setItem('w-footprint', step)
    // LAP-745: open the eco alimentation questionnaire directly on the dashboard
    // EAlimentationCard reads ?questionnaire=alimentation to auto-open the modal.
    navigate('/Dashboard?questionnaire=alimentation')
  }
  const Footprintfood = ({ data }) => {
    const [wfResultOpen, setWfResultOpen] = useState(false)
    const [wfResultData, setWfResultData] = useState(null)

    useEffect(() => {
      const raw = sessionStorage.getItem('w-footprint-data')
      if (!raw) return
      const numeric = parseFloat(raw)
      if (!Number.isNaN(numeric)) {
        setWfResultData(numeric)
        localStorage.setItem('w-footprint-done', 'true')
        setWfResultOpen(true)
      }
      sessionStorage.removeItem('w-footprint-data')
    }, [])

    const handleCloseWfResult = async () => {
      setWfResultOpen(false)
      await handleValidateStep()
    }

    const trees = wfResultData != null && wfResultData > 0 ? Math.floor(wfResultData / 0.025) : 0

    return (
      <>
        <Dialog
          open={wfResultOpen}
          onClose={handleCloseWfResult}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { bgcolor: '#1a1a2e', borderRadius: 2 } }}
        >
          <DialogContent>
            <Box color="white" p={1}>
              <Typography sx={{ fontSize: 20, fontWeight: 500, textAlign: 'center' }}>
                Votre impact carbone alimentaire en CO₂
              </Typography>
              <Box display="flex" justifyContent="center" gap={4} my={4} flexWrap="wrap">
                <Box display="flex" alignItems="center" gap={1}>
                  <img alt="" src="/fpicon.svg" />
                  <Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 600 }}>
                      {wfResultData != null ? wfResultData : 0}
                    </Typography>
                    <Typography>To CO₂ par an</Typography>
                  </Box>
                </Box>
                <Box display="flex" alignItems="center" gap={1}>
                  <img alt="" src="/icontree.svg" />
                  <Box>
                    <Typography sx={{ fontSize: 20, fontWeight: 600 }}>{trees}</Typography>
                    <Typography>{tLayout('wizardDialogs.treesNeeded')}</Typography>
                  </Box>
                </Box>
              </Box>
              <Typography textAlign="center" my={2}>
                Ce n'est qu'un début, dans Life votre empreinte carbone complète incluera également :
              </Typography>
              <Box
                my={4}
                display="flex"
                flexWrap="wrap"
                justifyContent="space-between"
                gap={2}
              >
                <Box display="flex" flexDirection="column" alignItems="center" flex="1 1 200px">
                  <img alt="" src="/iconfood.svg" style={{ height: 100 }} />
                  <Typography textAlign="center" mt={1}>
                    Alimentation via questionnaires
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center" flex="1 1 200px">
                  <img alt="" src="/iconcars.svg" style={{ height: 100 }} />
                  <Typography textAlign="center" mt={1}>
                    Véhicules et transports via kilométrage et dépenses
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center" flex="1 1 200px">
                  <img alt="" src="/iconhome.svg" style={{ height: 100 }} />
                  <Typography textAlign="center" mt={1}>
                    Habitation via caractéristiques du bien immobilier
                  </Typography>
                </Box>
                <Box display="flex" flexDirection="column" alignItems="center" flex="1 1 200px">
                  <img alt="" src="/iconshopping.svg" style={{ height: 100 }} />
                  <Typography textAlign="center" mt={1}>
                    Shopping via dépenses
                  </Typography>
                </Box>
              </Box>
              <Typography textAlign="center" my={2}>
                Ensemble, nous allons construire une image claire de votre impact environnemental et vous aider à l'optimiser.
              </Typography>
            </Box>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button onClick={handleCloseWfResult} variant="contained" sx={getButtonStyles('primary')}>
              Continuer
            </Button>
          </DialogActions>
        </Dialog>
        <StandardStepLayout
          data={data}
          buttons={[
            <Button key="action" variant="contained" sx={getButtonStyles('primary')} onClick={handelFoodPrint}>
              {data.text_button_1}
            </Button>,
            <Button key="valider" variant="contained" sx={getButtonStyles('primary')} onClick={handleValidateStep}>
              {tLayout('wizardButtons.validate')}
            </Button>,
            <Button key="skip" variant="contained" sx={getButtonStyles('primary')} onClick={handelSkip}>
              {data.text_button_2}
            </Button>,
          ]}
        />
      </>
    )
  }
  // Time_list step removed from the onboarding: the default lists are now
  // created automatically at account creation (backend, DEFAULT_USER_LISTS),
  // except the shopping list which is handled by the Courses app.
  const handelTprofile = () => {
    sessionStorage.setItem('w-tprofile', step)
    navigate('/syntheseTemps')
  }
  const TimeProfile = ({ data }) => {
    const [wtResultOpen, setWtResultOpen] = useState(false)
    const [wtResultData, setWtResultData] = useState(null)

    useEffect(() => {
      const profileName = sessionStorage.getItem('w-tprofile-result')
      if (!profileName) return
      // LAP-752 — same reverse-lookup as for the finance profile: backend may
      // return a localised time profile name; map it back to the FR canonical
      // before matching against TIME_PROFILE_OUTPUT.
      const canonical = findCanonicalProfileName(profileName, 'timeProfiles')
      const match = TIME_PROFILE_OUTPUT.find((p) => p.name === canonical)
      if (match) {
        setWtResultData(match)
        localStorage.setItem('user-tprofile', canonical)
        setWtResultOpen(true)
      }
      sessionStorage.removeItem('w-tprofile-result')
    }, [])

    const handleCloseWtResult = async () => {
      setWtResultOpen(false)
      await handleValidateStep()
    }

    return (
      <>
        <Dialog
          open={wtResultOpen}
          onClose={handleCloseWtResult}
          maxWidth="sm"
          fullWidth
          PaperProps={{ sx: { bgcolor: '#1a1a2e', borderRadius: 2 } }}
        >
          <DialogContent>
            {wtResultData && (
              <Box color="white" p={1}>
                <Typography sx={{ fontSize: 20, fontWeight: 500, textAlign: 'center' }}>
                  Votre profil de gestionnaire du temps est :
                </Typography>
                <Box display="flex" justifyContent="center" my={1} sx={{ fontSize: 100 }}>
                  {wtResultData.logo}
                </Box>
                <Typography sx={{ fontSize: 25, fontWeight: 700, textAlign: 'center', color: '#1d629e', mt: 1 }}>
                  {wtResultData.name}
                </Typography>
                <Typography sx={{ textAlign: 'center', lineHeight: 1.6, mt: 2 }}>
                  {wtResultData.text}
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
            <Button onClick={handleCloseWtResult} variant="contained" sx={getButtonStyles('primary')}>
              Continuer
            </Button>
          </DialogActions>
        </Dialog>
        <StandardStepLayout
          data={data}
          buttons={[
            <Button key="action" variant="contained" sx={getButtonStyles('primary')} onClick={handelTprofile}>
              {data.text_button_1}
            </Button>,
            <Button key="valider" variant="contained" sx={getButtonStyles('primary')} onClick={handleValidateStep}>
              {tLayout('wizardButtons.validate')}
            </Button>,
            <Button key="skip" variant="contained" sx={getButtonStyles('primary')} onClick={handelSkip}>
              {data.text_button_2}
            </Button>,
          ]}
        />
      </>
    )
  }
  // 4.11.4: Priority step removed – priorities are calculated from evaluations. Auto-skip if step still in flow.
  const TimePriorityAutoSkip = ({ data }) => {
    useEffect(() => {
      let cancelled = false
      const run = async () => {
        try {
          await wizardAPI.recalculatePriorities()
          if (cancelled) return
          await wizardAPI.updateWizardProgress({ step: 'Time_Priority', completed: true, skip: true })
          dispatch(setUserWizardDone('Time_Priority'))
          navigateAfterStepCompletion()
        } catch (e) {
          if (!cancelled) console.error('TimePriority auto-skip:', e)
        }
      }
      run()
      return () => { cancelled = true }
    }, [])
    return (
      <StandardStepLayout
        data={data}
        buttons={[]}
      >
        <Box py={4} textAlign="center" color="rgba(255,255,255,0.8)">
          <Typography>Calcul des priorités en cours...</Typography>
        </Box>
      </StandardStepLayout>
    )
  }

  // LAP-752 — Mirror_Elements removed from the onboarding flow (the same data
  // is now surfaced in the WheelOfLife at the end of the wizard). Existing
  // users may still have this step in their UserWizardProgress though, so we
  // keep the route handler but auto-advance immediately to the next step
  // without showing any UI. The step is marked as skipped so it stops being
  // proposed on subsequent loads.
  // Time_list / Perso_Target removed from the onboarding (lists are created by
  // default at signup; the 25-objectives definition step is gone). Existing
  // users may still carry these steps in their UserWizardProgress: auto-skip
  // them silently, same pattern as Mirror_Elements below.
  const ObsoleteStepAutoSkip = ({ stepKey }) => {
    useEffect(() => {
      let cancelled = false
      const run = async () => {
        try {
          await wizardAPI.updateWizardProgress({ step: stepKey, completed: true, skip: true })
          if (cancelled) return
          dispatch(setUserWizardSkip(stepKey))
          navigateAfterStepCompletion()
        } catch (e) {
          if (!cancelled) console.error(`${stepKey} auto-skip:`, e)
        }
      }
      run()
      return () => { cancelled = true }
    }, [stepKey])

    return (
      <StandardStepLayout data={{}} buttons={[]}>
        <Box py={6} textAlign="center" color="rgba(255,255,255,0.8)">
          <Typography>{tLayout('wizardButtons.continue')}…</Typography>
        </Box>
      </StandardStepLayout>
    )
  }

  const MirrorElementsAutoSkip = ({ data }) => {
    useEffect(() => {
      let cancelled = false
      const run = async () => {
        try {
          await wizardAPI.updateWizardProgress({
            step: 'Mirror_Elements',
            completed: true,
            skip: true,
          })
          if (cancelled) return
          dispatch(setUserWizardSkip('Mirror_Elements'))
          navigateAfterStepCompletion()
        } catch (e) {
          if (!cancelled) console.error('Mirror_Elements auto-skip:', e)
        }
      }
      run()
      return () => { cancelled = true }
    }, [])

    return (
      <StandardStepLayout data={data} buttons={[]}>
        <Box py={6} textAlign="center" color="rgba(255,255,255,0.8)">
          <Typography>{tLayout('wizardButtons.continue')}…</Typography>
        </Box>
      </StandardStepLayout>
    )
  }

  const handelExperience = () => {
    localStorage.setItem('w-experence', step)
    navigate('/parcourspro')
  }
  const Workexp = ({ data }) => (
    <StandardStepLayout 
      data={data}
      buttons={[
        <Button key="action" variant="contained" sx={getButtonStyles('primary')} onClick={handelExperience}>
              {data.text_button_1}
        </Button>,
        <Button key="valider" variant="contained" sx={getButtonStyles('primary')} onClick={handleValidateStep}>
          {tLayout('wizardButtons.validate')}
        </Button>,
        <Button key="skip" variant="contained" sx={getButtonStyles('primary')} onClick={handelSkip}>
              {data.text_button_2}
            </Button>
      ]}
    />
  )
  // Perso_Target (définition des 25 objectifs) removed from the onboarding.
  // Objectives are now created contextually (evaluation steps, quick add,
  // Mes Objectifs) rather than in a dedicated wizard step.

  const TIME_DONUT_COLORS = ['#0089ff', '#2ca948', '#e67e22', '#9b59b6', '#e74c3c', '#1abc9c', '#f39c12', '#3498db', '#e91e63', '#00bcd4', '#8bc34a', '#ff9800']

  // Time Breakdown Step Component (Spec 4.12) – two interfaces: current hours then desired %
  const TimeBreakdown = ({ data }) => {
    const [breakdown, setBreakdown] = useState([])
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const currentStep = data?.message_key || step
    const isFirstScreen = currentStep === 'Time_Breakdown'

    useEffect(() => {
      loadTimeBreakdown()
    }, [])

    const loadTimeBreakdown = async () => {
      try {
        setLoading(true)
        const res = await wizardAPI.getTimeBreakdown()
        const raw = Array.isArray(res) ? res : []
        const normalized = raw.map((item) => {
          const cur = Number(item?.current_hours)
          const des = Number(item?.desired_hours)
          return {
            ...item,
            id: item?.id,
            domain: item?.domain ?? '',
            current_hours: Number.isFinite(cur) ? cur : 0,
            desired_hours: Number.isFinite(des) ? des : 0,
          }
        })
        setBreakdown(normalized)
      } catch (error) {
        console.error('Error loading time breakdown:', error)
        setBreakdown([])
        alert(tLayout('wizardErrors.loadData'))
      } finally {
        setLoading(false)
      }
    }

    const handleCurrentHoursChange = (index, value) => {
      const v = Math.max(0, Math.min(168, parseFloat(value) || 0))
      const updated = [...breakdown]
      updated[index] = { ...updated[index], current_hours: v }
      setBreakdown(updated)
    }

    const handleDesiredPercentChange = (index, value) => {
      const v = Math.max(0, Math.min(100, parseFloat(value) || 0))
      const updated = [...breakdown]
      updated[index] = { ...updated[index], desired_percent: v }
      setBreakdown(updated)
    }

    const toNum = (v) => {
      const n = parseFloat(v)
      return Number.isFinite(n) ? n : 0
    }
    const totalCurrentHours = breakdown.reduce((s, i) => s + toNum(i.current_hours), 0)
    const totalDesiredPercent = breakdown.reduce((s, i) => {
      const pct = i.desired_percent != null ? toNum(i.desired_percent) : (toNum(i.desired_hours) / 168) * 100
      return s + (Number.isFinite(pct) ? pct : 0)
    }, 0)

    const handleSave = async () => {
      setSaving(true)
      try {
        const payload = breakdown.map((item) => {
          const cur = toNum(item.current_hours)
          const desiredPct = item.desired_percent != null ? toNum(item.desired_percent) : (toNum(item.desired_hours) / 168) * 100
          const desiredH = isFirstScreen ? toNum(item.desired_hours) : (Number.isFinite(desiredPct) ? desiredPct : 0) / 100 * 168
          return { id: item.id, domain: item.domain, current_hours: cur, desired_hours: desiredH }
        })
        await wizardAPI.saveTimeBreakdown(payload)
        await wizardAPI.updateWizardProgress({ step: currentStep, completed: true, skip: false })
        await dispatch(setUserWizardDone(currentStep))
        await navigateAfterStepCompletion()
      } catch (error) {
        console.error('Error saving time breakdown:', error)
        alert(tLayout('wizardErrors.save'))
      } finally {
        setSaving(false)
      }
    }

    const handleSkipTime = async () => {
      try {
        await wizardAPI.updateWizardProgress({ step: currentStep, completed: false, skip: true })
        await dispatch(setUserWizardSkip(currentStep))
        await navigateAfterStepCompletion()
      } catch (e) {
        console.error(e)
      }
    }

    const inputSx = {
      '& .MuiOutlinedInput-root': { color: 'white', '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' } },
      '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.7)' },
    }
    const donutCurrentData = breakdown
      .map((item, idx) => ({
        name: item.domain || '',
        value: totalCurrentHours > 0 ? Math.round((toNum(item.current_hours) / totalCurrentHours) * 100) : 0,
        hours: toNum(item.current_hours),
        color: TIME_DONUT_COLORS[idx % TIME_DONUT_COLORS.length],
      }))
      .filter((d) => d.hours > 0)
    const donutDesiredData = breakdown
      .map((item, idx) => ({
        name: item.domain || '',
        value: Math.round(item.desired_percent != null ? toNum(item.desired_percent) : (toNum(item.desired_hours) / 168) * 100),
        color: TIME_DONUT_COLORS[idx % TIME_DONUT_COLORS.length],
      }))
      .filter((d) => d.value > 0)
    const donutCurrentFromHoursData = breakdown
      .map((item, idx) => ({
        name: item.domain || '',
        value: totalCurrentHours > 0 ? Math.round((toNum(item.current_hours) / 168) * 100) : 0,
        color: TIME_DONUT_COLORS[idx % TIME_DONUT_COLORS.length],
      }))
      .filter((d) => d.value > 0)

    const renderDonut = (pieData, title) => (
      <Box sx={{ flex: 1, minWidth: 200 }}>
        <Typography variant="subtitle2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 1, textAlign: 'center' }}>{title}</Typography>
        <ResponsiveContainer width="100%" height={220}>
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={90}
              paddingAngle={2}
              label={({ name, value }) => (value > 0 ? `${name} ${value}%` : '')}
            >
              {pieData.map((entry) => (
                <Cell key={entry.name} fill={entry.color} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{ backgroundColor: '#1a1a2e', border: '1px solid rgba(255,255,255,0.2)', borderRadius: 4 }}
              formatter={(value, name) => [`${value}%`, name]}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
    )

    if (loading) {
      return (
        <StandardStepLayout data={data} buttons={[]}>
          <Box textAlign="center" py={4}>
            <Typography color="white">Chargement...</Typography>
          </Box>
        </StandardStepLayout>
      )
    }

    return (
      <StandardStepLayout
        data={{
          ...data,
          title_content: 'Répartition du temps actuel vs cible',
          subtitle: 'GESTION DU TEMPS',
          text_content: undefined,
        }}
        buttons={[
          <Button key="continue" variant="contained" sx={getButtonStyles('primary')} onClick={handleSave} disabled={saving}>
            Continuer
          </Button>,
          <Button key="skip" variant="outlined" sx={getButtonStyles('secondary')} onClick={handleSkipTime} disabled={saving}>
            Passer
          </Button>,
        ]}
      >
        <Box mt={2}>
          <Typography variant="h6" sx={{ color: 'white', fontWeight: 600, mb: 1 }}>
            {isFirstScreen ? 'Ta réalité actuelle' : 'Ton idéale de vie'}
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.85)', mb: 3, lineHeight: 1.6 }}>
            {isFirstScreen
              ? "Indique combien d'heures par semaine tu consacres à chaque domaine de ta vie. Ce sera notre point de départ pour mieux t'aider à équilibrer ton quotidien. Pas besoin d'être précis à la minute près, fais au mieux selon ton ressenti !"
              : "Maintenant, imagine la vie que tu aimerais vivre. Quelle place souhaites-tu accorder à chaque domaine ? En t'appuyant sur tes objectifs, définis ta répartition de temps idéale. Life t'aidera à t'en rapprocher, pas à pas."}
          </Typography>

          {isFirstScreen ? (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 3 }}>
                {breakdown.map((item, index) => (
                  <Box key={item.id || index}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                      <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: TIME_DONUT_COLORS[index % TIME_DONUT_COLORS.length] }} />
                      <Typography sx={{ color: 'white', minWidth: 100 }}>{item.domain}</Typography>
                      <Typography sx={{ color: '#0089ff', fontWeight: 600 }}>{Math.round(toNum(item.current_hours))} h</Typography>
                    </Box>
                    <Slider
                      value={Math.min(168, Math.max(0, toNum(item.current_hours)))}
                      onChange={(_, v) => handleCurrentHoursChange(index, v)}
                      min={0}
                      max={168}
                      step={1}
                      valueLabelDisplay="auto"
                      valueLabelFormat={(v) => `${v} h`}
                      sx={{ color: '#0089ff', '& .MuiSlider-thumb': { width: 20, height: 20 }, '& .MuiSlider-rail': { opacity: 0.5 } }}
                    />
                  </Box>
                ))}
              </Box>
              <Typography sx={{ color: totalCurrentHours === 168 ? '#2ca948' : 'rgba(255,255,255,0.9)', mb: 2 }}>
                Total : {Number.isFinite(totalCurrentHours) ? Math.round(totalCurrentHours) : 0} h / 168 h
                {totalCurrentHours !== 168 && (
                  <Typography component="span" sx={{ color: '#e74c3c', ml: 1 }}>
                    Ajuste pour correspondre à ta semaine (168 h).
                  </Typography>
                )}
              </Typography>
              {donutCurrentData.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', mb: 2 }}>
                  {renderDonut(donutCurrentData, 'Répartition actuelle')}
                </Box>
              )}
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 3 }}>
                {breakdown.map((item, index) => {
                  const pct = item.desired_percent != null ? toNum(item.desired_percent) : Math.round((toNum(item.desired_hours) / 168) * 100)
                  const pctSafe = Number.isFinite(pct) ? Math.min(100, Math.max(0, pct)) : 0
                  return (
                    <Box key={item.id || index}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 0.5 }}>
                        <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: TIME_DONUT_COLORS[index % TIME_DONUT_COLORS.length] }} />
                        <Typography sx={{ color: 'white', minWidth: 100 }}>{item.domain || ''}</Typography>
                        <Typography sx={{ color: '#0089ff', fontWeight: 600 }}>{Math.round(pctSafe)} %</Typography>
                      </Box>
                      <Slider
                        value={pctSafe}
                        onChange={(_, v) => handleDesiredPercentChange(index, v)}
                        min={0}
                        max={100}
                        step={1}
                        valueLabelDisplay="auto"
                        valueLabelFormat={(v) => `${v} %`}
                        sx={{ color: '#0089ff', '& .MuiSlider-thumb': { width: 20, height: 20 }, '& .MuiSlider-rail': { opacity: 0.5 } }}
                      />
                    </Box>
                  )
                })}
              </Box>
              <Typography sx={{ color: totalDesiredPercent === 100 ? '#2ca948' : 'rgba(255,255,255,0.9)', mb: 2 }}>
                Total : {Number.isFinite(totalDesiredPercent) ? Math.round(totalDesiredPercent) : 0} %
                {totalDesiredPercent !== 100 && (
                  <Typography component="span" sx={{ color: '#e74c3c', ml: 1 }}>
                    Ajuste tes curseurs pour atteindre 100 %.
                  </Typography>
                )}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, justifyContent: 'center', mb: 2 }}>
                {donutDesiredData.length > 0 && renderDonut(donutDesiredData, 'Répartition souhaitée')}
                {donutCurrentFromHoursData.length > 0 && renderDonut(donutCurrentFromHoursData, 'Répartition actuelle')}
              </Box>
            </>
          )}
        </Box>
      </StandardStepLayout>
    )
  }

  // Professional Status Step Component (LAP-597)
  const ProfessionalStatus = ({ data }) => {
    const [saving, setSaving] = useState(false)
    // Fallbacks so "Étudiant" (student) is always available even if API returns null/empty
    const defaultOptions = ['Actif salarié', 'En recherche d\'emploi', 'Étudiant', 'Retraité', 'Rentier', 'Invalide']
    const statusOptions = [
      safeDisplay(data?.text_button_1) || defaultOptions[0],
      safeDisplay(data?.text_button_2) || defaultOptions[1],
      safeDisplay(data?.text_button_3) || defaultOptions[2],
      safeDisplay(data?.text_button_4) || defaultOptions[3],
      safeDisplay(data?.text_button_5) || defaultOptions[4],
      safeDisplay(data?.text_button_6) || defaultOptions[5],
    ].filter((label) => label && label.length > 0)
    const options = statusOptions.length >= 3 ? statusOptions : defaultOptions

    const handleStatusSelect = async (status) => {
      if (!status || saving) return
      setSaving(true)
      try {
        await wizardAPI.saveProfessionalStatus({ status })
        await wizardAPI.updateWizardProgress({ step: 'ONBOARDING_PRO_STATUT', completed: true, skip: false })
        await dispatch(setUserWizardDone('ONBOARDING_PRO_STATUT'))
        await navigateAfterStepCompletion()
      } catch (error) {
        console.error('Error saving professional status:', error)
        alert(tLayout('wizardErrors.save'))
      } finally {
        setSaving(false)
      }
    }

    return (
      <StandardStepLayout
        data={data}
        buttons={options.map((status, index) => (
          <Button
            key={index}
            type="button"
            variant="contained"
            sx={{
              ...getButtonStyles('primary'),
              width: '100%',
              minHeight: 48,
            }}
            onClick={() => handleStatusSelect(status)}
            disabled={saving}
          >
            {status}
          </Button>
        ))}
      />
    )
  }

  return (
    <>
      <Box
        sx={{
          minHeight: '100vh',
          backgroundColor: '#00001d',
          backgroundImage: 'radial-gradient(circle at top, rgba(0, 29, 110, 0.3) 0%, #00001d 100%)',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: 'url(/small.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            opacity: 0.1,
            zIndex: 0,
          },
        }}
      >
        <Box 
          paddingX={{ xs: 2, sm: 4, md: 8, lg: 15 }}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          {loading ? (
            <Box 
              display="flex" 
              flexDirection="column"
              justifyContent="center" 
              alignItems="center" 
              minHeight="85vh"
              color="white"
              gap={2}
            >
              <Box
                sx={{
                  width: 50,
                  height: 50,
                  border: '4px solid rgba(255, 255, 255, 0.1)',
                  borderTop: '4px solid #0089ff',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                  '@keyframes spin': {
                    '0%': { transform: 'rotate(0deg)' },
                    '100%': { transform: 'rotate(360deg)' },
                  },
                }}
              />
              <Typography fontSize={18} color="rgba(255, 255, 255, 0.8)">Chargement...</Typography>
        </Box>
          ) : (
            <>
              {wizard?.message_key === 'Welcome' && wizard && <Welcome data={wizard} />}
              {wizard?.message_key === 'Social_Contact' && wizard && <SocialContact data={wizard} />}
              {wizard?.message_key === 'Finance_Account' && wizard && <FinanceAccount data={wizard} />}
              {wizard?.message_key === 'Finance_knowledge' && wizard && <Financeknowledge data={wizard} />}
              {wizard?.message_key === 'Finance_budget' && wizard && <Financebudget data={wizard} />}
              {wizard?.message_key === 'Finance_Interest' && wizard && <FinanceInterest data={wizard} />}
              {wizard?.message_key === 'Finance_Profile' && wizard && <FinanceProfile data={wizard} />}
              {wizard?.message_key === 'Home_REVH' && wizard && <HomeREVH data={wizard} />}
              {wizard?.message_key === 'Footprint_food' && wizard && <Footprintfood data={wizard} />}
              {wizard?.message_key === 'Time_Profile' && wizard && <TimeProfile data={wizard} />}
              {wizard?.message_key === 'Time_Priority' && wizard && <TimePriorityAutoSkip data={wizard} />}
              {wizard?.message_key === 'Work_exp' && wizard && <Workexp data={wizard} />}
              {wizard?.message_key === 'ONBOARDING_PRO_STATUT' && wizard && <ProfessionalStatus data={wizard} />}
              {(wizard?.message_key === 'Time_Breakdown' || wizard?.message_key === 'Time_TargetBreakdown') && wizard && <TimeBreakdown data={wizard} />}
              {wizard?.message_key === 'Finance_Profile_Summary' && wizard && <FinancialProfileSummary data={wizard} />}
              {wizard?.message_key === 'Eco_Footprint_Summary' && wizard && <EcoFootprintSummary data={wizard} />}
              {wizard?.message_key === 'Time_Management_Summary' && wizard && <TimeManagementProfileSummary data={wizard} />}
              {wizard?.message_key === 'Professional_Experience_Summary' && wizard && <ProfessionalExperienceSummary data={wizard} />}
              {wizard?.message_key === 'Mirror_Elements' && wizard && <MirrorElementsAutoSkip data={wizard} />}
              {wizard?.message_key === 'Time_list' && wizard && <ObsoleteStepAutoSkip stepKey="Time_list" />}
              {wizard?.message_key === 'Perso_Target' && wizard && <ObsoleteStepAutoSkip stepKey="Perso_Target" />}
              {/* GE015 v1.6 + Activation Life — final wizard flow:
                  Endwelcome → UserPortrait (Mirror) → WheelOfLife →
                  ActivationFlow (priorities → intention → Life Plan) → SmartFeed.
                  Each step's CTA flips one boolean and renders the next view
                  in the same wizard frame, so the user perceives a linear
                  single-click progression. The ActivationFlow now owns the
                  final handelFinsh call (was: the Wheel of Life CTA). */}
              {wizard?.message_key === 'End_welcome' && wizard && (
                showActivation ? (
                  <ActivationFlow onFinish={handelFinsh} />
                ) : showWheelOfLife ? (
                  <WheelOfLife onContinue={() => setShowActivation(true)} />
                ) : showUserPortrait ? (
                  <UserPortrait onGoToLife={() => setShowWheelOfLife(true)} />
                ) : (
                  <Endwelcome data={wizard} onDiscoverPortrait={() => setShowUserPortrait(true)} />
                )
              )}
              {/* Evaluation Steps - Use StandardStepLayout with skip button */}
              {wizard?.message_key === 'Social_Evaluation' && wizard && <EvaluationStep data={wizard} stepKey="Social_Evaluation" />}
              {wizard?.message_key === 'Finance_Evaluation' && wizard && <EvaluationStep data={wizard} stepKey="Finance_Evaluation" />}
              {wizard?.message_key === 'Home_Evaluation' && wizard && <EvaluationStep data={wizard} stepKey="Home_Evaluation" />}
              {wizard?.message_key === 'Footprint_Evaluation' && wizard && <EvaluationStep data={wizard} stepKey="Footprint_Evaluation" />}
              {wizard?.message_key === 'Time_Evaluation' && wizard && <EvaluationStep data={wizard} stepKey="Time_Evaluation" />}
              {wizard?.message_key === 'Work_Evaluation' && wizard && <EvaluationStep data={wizard} stepKey="Work_Evaluation" />}
              {wizard?.message_key === 'Perso_Evaluation' && wizard && <EvaluationStep data={wizard} stepKey="Perso_Evaluation" />}
              {wizard?.message_key === 'Admin_Evaluation' && wizard && <EvaluationStep data={wizard} stepKey="Admin_Evaluation" />}
              {/* Personal Development Steps - Use EvaluationStep component for steps with empty content and skip button */}
              {wizard?.message_key === 'Perso_Sport' && wizard && <SportStep data={wizard} />}
              {wizard?.message_key === 'Perso_Well_being' && wizard && <HealthStep data={wizard} />}
              {wizard?.message_key === 'Perso_Leisure' && wizard && <LeisureStep data={wizard} />}
            </>
          )}
        </Box>
      </Box>
      
      {/* Skip All Confirmation Dialog (spec 4.11.1, 4.11.2) - Enhanced */}
      <Dialog 
        open={skipAllConfirmOpen} 
        onClose={cancelSkipAll}
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: '#1a1a2e',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
            backgroundImage: 'linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(255, 152, 0, 0.1) 100%)',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: 'white',
            fontSize: { xs: 20, md: 24 },
            fontWeight: 600,
            pb: 1,
          }}
        >
          {tLayout('wizardConfirm.skipAll.title')}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: { xs: 15, md: 17 },
              lineHeight: 1.6,
            }}
          >
            {tLayout('wizardConfirm.skipAll.body')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button
            onClick={cancelSkipAll}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              textTransform: 'none',
              fontSize: 16,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {tLayout('wizardConfirm.skipAll.cancel')}
          </Button>
          <Button
            onClick={confirmSkipAll}
            variant="contained"
            sx={{
              backgroundColor: '#e91e63',
              background: 'linear-gradient(135deg, #e91e63 0%, #f06292 100%)',
              color: 'white',
              textTransform: 'none',
              fontSize: 16,
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 1.5,
              boxShadow: '0 4px 12px rgba(233, 30, 99, 0.3)',
              '&:hover': {
                backgroundColor: '#c2185b',
                background: 'linear-gradient(135deg, #c2185b 0%, #e91e63 100%)',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(233, 30, 99, 0.4)',
              },
            }}
          >
            {tLayout('wizardConfirm.skipAll.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Skip Step Confirmation Dialog (spec 4.11.2) - Enhanced */}
      <Dialog 
        open={skipConfirmOpen} 
        onClose={cancelSkip}
        PaperProps={{
          sx: {
            borderRadius: 3,
            backgroundColor: '#1a1a2e',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
          },
        }}
      >
        <DialogTitle
          sx={{
            color: 'white',
            fontSize: { xs: 20, md: 24 },
            fontWeight: 600,
            pb: 1,
          }}
        >
          {tLayout('wizardConfirm.skipStep.title')}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: { xs: 15, md: 17 },
              lineHeight: 1.6,
            }}
          >
            {tLayout('wizardConfirm.skipStep.body')}
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1.5 }}>
          <Button
            onClick={cancelSkip}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              textTransform: 'none',
              fontSize: 16,
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              },
            }}
          >
            {tLayout('wizardConfirm.skipStep.cancel')}
          </Button>
          <Button
            onClick={confirmSkip}
            variant="contained"
            sx={{
              backgroundColor: '#0089ff',
              background: 'linear-gradient(135deg, #0089ff 0%, #00a8ff 100%)',
              color: 'white',
              textTransform: 'none',
              fontSize: 16,
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: 1.5,
              boxShadow: '0 4px 12px rgba(0, 137, 255, 0.3)',
              '&:hover': {
                backgroundColor: '#0070cc',
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 16px rgba(0, 137, 255, 0.4)',
              },
            }}
          >
            {tLayout('wizardConfirm.skipStep.confirm')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default UserWizardPage
